/**
 * Job Detail Page
 *
 * Shows a single job with its station entries and allows:
 * - Auto-matching flighting codes to existing tracks
 * - Manual linking of tracks to stations
 * - Navigating to Add Track to upload missing spots
 * - Viewing job status and delivery progress
 */

import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from 'reactstrap';
import moment from 'moment';
import PageContainer from '../../components/global/PageContainer';
import { withAuthorization, withOrganization } from '../../components/Session';
import { FirebaseContext } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { getCampaignName } from '../../utils/campaign';
import { useTracksStore } from '../../store/tracks.store';
import { doc, getDoc, updateDoc, collection, addDoc, getDocs, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getOrgCollectionPath } from '../../constants/paths';
import { getAuth } from 'firebase/auth';
import { generateClaimCode } from '../../types/license';
import ChaseListUploader from '../../components/MediaPlan/ChaseListUploader';
import { useToast } from '../../contexts/ToastContext';
import { canShareBatchForReview } from '../../utils/reviewShareGate';
import { useCollectionCache } from '../../services/collectionCache.service';
import { CampaignChange, CampaignHistoryEntry, describeChange } from '../../utils/campaignHistory';
import DownloadManagerModal from '../../components/DownloadManagerModal/DownloadManagerModal';
import WavePlayer from '../../components/WavePlayer';
import VersionBadge from '../../components/VersionBadge/VersionBadge';
// [FTP-MIGRATION 2026-06-15] Send-to-broadcaster-FTP removed from the
// campaign delivery flow. FTP is now triggered from the Download Manager and
// always uploads to the *current org's own* FTP. The original delivery-time
// modal, its target-gathering helper, and the associated state are left
// commented below — see docs/FTP_DOWNLOAD_MANAGER_MIGRATION.md.
// import FtpDeliveryModal, { FtpDeliveryTarget } from '../../components/FTPSettings/FtpDeliveryModal';
import FtpCampaignControl, { FtpCampaignControlHandle } from '../../components/FTPSettings/FtpCampaignControl';
import { downloadDeliveryReportPdf, downloadDeliveryReportExcel, downloadDeliveryReportCsv } from '../../services/deliveryReport';
import * as ROUTES from '../../constants/routes';

const QC_SERVICE_URL = process.env.REACT_APP_QC_SERVICE_URL || 'http://localhost:8080';

interface StationEntry {
  /** Stable per-row identity assigned at first parse / save. Optional
   *  because legacy stations pre-dating the id introduction may not
   *  have it — those get backfilled on the next save via
   *  ChaseListUploader's persist path. Used by the delivery pipeline
   *  to match rows across source, campaign copies, delivery records,
   *  and broadcaster copies without relying on array index or on
   *  potentially-duplicate composite keys (flighting code + station
   *  name). Backfilled from parseCampaignEntry / handleSave. */
  id?: string;
  station: string;
  mediaOwner: string;
  language: string;
  duration: string;
  flightingCode: string;
  flightingDate: string;
  materialDeadline: string;
  contact: string;
  title: string;
  linkedTrackId: string | null;
  deliveryStatus: string;
  // Fields populated by the chase list parser / picker; optional because older
  // jobs may not have them. Used by the delivery-time language-acceptance check.
  stationOrgId?: string | null;
  stationOrgName?: string | null;
  mediaOwnerOrgId?: string | null;
  mediaOwnerOrgName?: string | null;
  languageOptions?: { label: string; value: string }[];
  /** Live Read spot — no audio to link (script-read by the DJ). Detected
   *  from the flighting-code suffix (`LR` / `L_R` / `L`) at parse time and
   *  persisted on the station so downstream consumers (auto-match,
   *  delivery gate, row rendering) don't have to re-derive it. */
  isLiveRead?: boolean;
}

interface JobData {
  id: string;
  client: string;
  campaign: string;
  period: string;
  date: string;
  revision: string;
  status: string;
  stations: StationEntry[];
  stationCount: number;
  linkedCount: number;
  uploadedAt: number;
  uploadedByName: string;
  fileName: string;
  agencyContacts?: any[];
  isDeliveredJob?: boolean;
  sourceOrgName?: string;
}

const JobDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const history = useHistory();
  const location = useLocation();
  const firebase = useContext(FirebaseContext);
  const { currentOrgId, currentOrgName } = useOrganizationStore();
  const bypassQc = useOrganizationStore(state => state.currentOrgBypassQc);
  const bypassReview = useOrganizationStore(state => state.currentOrgBypassReview);
  // Org-level rule: when off (default), every linked track must pass QC before
  // a batch share-for-review can be initiated. See utils/reviewShareGate.
  const bypassQcBeforeReview = useOrganizationStore(state => state.currentOrgBypassQcBeforeReview);
  const { showWarning } = useToast();
  // Global broadcaster cache — used for the language-acceptance check below
  // so we don't have to chunk-query Firestore each time the user hits Share.
  const cachedBroadcasters = useCollectionCache((s) => s.broadcaster);

  const [job, setJob] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [matchResult, setMatchResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [publishingUpdates, setPublishingUpdates] = useState(false);
  const [shareResult, setShareResult] = useState<{ deliveries: any[]; errors: string[] } | null>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  // Standalone Delivery-FTP summary for this job (host + sent flighting codes),
  // written by the onDeliveryCreatedSendToFtp Cloud Function. Rendered as its
  // own row in the deliveries list — not as a per-station badge.
  const [ftpDeliveryState, setFtpDeliveryState] = useState<any | null>(null);
  // Imperative handle to the per-campaign FTP control — handleShareJob calls
  // onBeforeDeliver() right before creating deliveries (adds a newly-opted-in
  // client to the FTP list so the delivery trigger picks it up).
  const ftpControlRef = useRef<FtpCampaignControlHandle>(null);
  const [showDownloadManager, setShowDownloadManager] = useState(false);
  const [playingStationIdx, setPlayingStationIdx] = useState<number | null>(null);
  const [batchQCing, setBatchQCing] = useState(false);
  const [batchQCProgress, setBatchQCProgress] = useState({ current: 0, total: 0, results: '' });
  const [batchReviewing, setBatchReviewing] = useState(false);
  const [batchReviewProgress, setBatchReviewProgress] = useState({ current: 0, total: 0 });
  const [showBatchReviewModal, setShowBatchReviewModal] = useState(false);
  const [batchReviewTarget, setBatchReviewTarget] = useState<any>(null);
  const [batchReviewTargetSearch, setBatchReviewTargetSearch] = useState('');
  const [batchReviewTargetOptions, setBatchReviewTargetOptions] = useState<any[]>([]);
  const [batchReviewNotes, setBatchReviewNotes] = useState('');
  const [batchReviewLink, setBatchReviewLink] = useState<string | null>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  // Activity panel — collapsible campaign history. Entries live at
  // /organizations/{sourceOrgId}/jobs/{sourceJobId}/history. Subscribed
  // once we know where the source is (see effect below).
  const [historyEntries, setHistoryEntries] = useState<CampaignHistoryEntry[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  // [FTP-MIGRATION 2026-06-15] State for the delivery-time FTP modal is
  // kept commented out; can be revived if the broadcaster-FTP delivery flow
  // is ever re-enabled.
  // const [ftpModalOpen, setFtpModalOpen] = useState(false);
  // const [ftpTargets, setFtpTargets] = useState<FtpDeliveryTarget[]>([]);
  // const [ftpChecking, setFtpChecking] = useState(false);

  // Get tracks from the store for matching
  const tracks = useTracksStore(state => state.tracks);

  // Is this a received (delivered) job vs one we created?
  const isReceivedJob = !!job?.isDeliveredJob;
  // Campaign copy state — plus a role-based capability derivation. The
  // matrix follows the client's model:
  //   - Source (no copy flag) or master-org copy: full controls
  //   - copyRole 'studio': full controls (studio can act on their copy)
  //   - copyRole 'agency': Edit only (agency merely maintains campaign
  //     metadata; QC / Review / Deliver stay source-side)
  //   - copyRole 'creator': existing (Upload + Auto-Match only unless
  //     the current org is the master, in which case master exception
  //     grants full via `isMasterOrg` elsewhere)
  const isCampaignCopy = !!(job as any)?.isCampaignCopy;
  const jobCopyRole: 'studio' | 'agency' | 'creator' | null =
    ((job as any)?.copyRole as any) || null;
  // Which actions the current viewer can take on THIS campaign doc.
  // Only the flags used to gate menu items live here.
  // Builds the actor snapshot used on history entries. Always run after
  // job load — currentOrgName + auth are stable throughout a session.
  const buildActor = () => {
    const auth = getAuth();
    const u = auth.currentUser;
    return {
      actorUid: u?.uid || '',
      actorName: u?.displayName || u?.email || '',
      actorOrgId: currentOrgId || '',
      actorOrgName: currentOrgName || '',
      actorOrgRole: (isCampaignCopy ? (jobCopyRole || 'creator') : 'source') as 'source' | 'studio' | 'agency' | 'creator',
    };
  };
  const canEditCampaign = !isCampaignCopy || jobCopyRole === 'studio' || jobCopyRole === 'agency';
  const canUploadSpots = !isCampaignCopy || jobCopyRole === 'studio' || jobCopyRole === 'creator';
  const canAutoMatch = !isCampaignCopy || jobCopyRole === 'studio' || jobCopyRole === 'creator';
  const canBatchQc = !isCampaignCopy || jobCopyRole === 'studio';
  const canBatchReview = !isCampaignCopy || jobCopyRole === 'studio';
  const canDeliver = !isCampaignCopy || jobCopyRole === 'studio';

  // Live-subscribe to the job doc — download-badge fields
  // (deliveryCount / downloadedDeliveryCount / broadcasterDownloadStatus)
  // are written by the stampDeliveryDownload CF asynchronously after
  // a broadcaster downloads. A one-time fetch would leave the
  // per-station badges + aggregate badge stale until page refresh.
  useEffect(() => {
    if (!currentOrgId || !firebase || !jobId) {
      setLoading(false);
      return;
    }
    const jobRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'), jobId);
    const unsub = onSnapshot(jobRef, (jobDoc) => {
      if (jobDoc.exists()) {
        setJob({ id: jobDoc.id, ...jobDoc.data() } as JobData);
        setError(null);
      } else {
        setError('Campaign not found');
      }
      setLoading(false);
    }, (err: any) => {
      setError(`Failed to load job: ${err.message}`);
      setLoading(false);
    });
    return () => unsub();
  }, [currentOrgId, firebase, jobId]);

  // Auto-match on return from Add Track page
  useEffect(() => {
    const state = location.state as { autoMatch?: boolean } | undefined;
    if (state?.autoMatch && job && !loading && !matching) {
      // Clear the state so it doesn't re-trigger
      history.replace(location.pathname, {});
      handleAutoMatch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, loading, location.state]);

  // Live-subscribe to deliveries so ftpStatus / accepted-state changes reflect without a refresh
  useEffect(() => {
    if (!currentOrgId || !firebase || !jobId) return;
    const ref = collection(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'), jobId, 'deliveries');
    const unsub = onSnapshot(ref, (snap: any) => {
      const list = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      setDeliveries(list);
    }, (err: any) => {
      console.error('[JobDetail] deliveries listener error:', err);
    });
    return () => unsub();
  }, [currentOrgId, firebase, jobId]);

  // Live-subscribe to this job's Delivery-FTP summary so the standalone FTP
  // delivery row updates without a refresh. Lives on the source org's job.
  useEffect(() => {
    if (!currentOrgId || !firebase || !jobId) return;
    const ref = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'), jobId, 'ftpDeliveryState', 'state');
    const unsub = onSnapshot(ref, (snap: any) => {
      setFtpDeliveryState(snap.exists() ? snap.data() : null);
    }, (err: any) => {
      console.error('[JobDetail] ftpDeliveryState listener error:', err);
    });
    return () => unsub();
  }, [currentOrgId, firebase, jobId]);

  // Activity panel — real-time subscription to the campaign's history log.
  // The log lives on the SOURCE org, so we resolve sourceOrgId/sourceJobId
  // when viewing a copy. Cleaned up on unmount / job switch.
  useEffect(() => {
    if (!job || !firebase) {
      setHistoryEntries([]);
      return;
    }
    const isCopy = !!(job as any).isCampaignCopy;
    const sourceOrgIdForHistory = isCopy ? ((job as any).sourceOrgId || currentOrgId) : currentOrgId;
    const sourceJobIdForHistory = isCopy ? ((job as any).sourceJobId || jobId) : jobId;
    if (!sourceOrgIdForHistory) {
      setHistoryEntries([]);
      return;
    }
    const historyRef = collection(
      firebase.db,
      getOrgCollectionPath(sourceOrgIdForHistory, 'jobs'),
      sourceJobIdForHistory,
      'history',
    );
    const q = query(historyRef, orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const entries: CampaignHistoryEntry[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      }));
      setHistoryEntries(entries);
    }, (err) => {
      console.warn('History subscription failed:', err);
    });
    return () => { try { unsub(); } catch { /* ignore */ } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, jobId, currentOrgId, firebase]);

  // Refresh cross-org received copies (delivery OR campaign) from source org —
  // picks up QC/review/file changes made by the artist that owns the audio.
  useEffect(() => {
    if (!job || !firebase || !currentOrgId) return;
    const refreshDeliveryTracks = async () => {
      const linkedIds = Array.from(new Set(job.stations.filter(s => s.linkedTrackId).map(s => s.linkedTrackId!)));
      for (const trackId of linkedIds) {
        const localTrack = tracks[trackId];
        if (!localTrack) continue;
        const isReceivedCopy = (localTrack.isDeliveryTrack || (localTrack as any).isCampaignCopy)
          && localTrack.sourceOrgId
          && localTrack.sourceOrgId !== currentOrgId;
        if (!isReceivedCopy) continue;
        try {
          const sourceRef = doc(firebase.db, getOrgCollectionPath(localTrack.sourceOrgId, 'audiometa'), trackId);
          const sourceSnap = await getDoc(sourceRef);
          if (!sourceSnap.exists()) continue;
          const sourceData = sourceSnap.data();
          // Sync key status fields from source to local copy if they differ
          const updates: any = {};
          if (sourceData.qc_status && sourceData.qc_status !== localTrack.qc_status) updates.qc_status = sourceData.qc_status;
          if (sourceData.approval_status && sourceData.approval_status !== localTrack.approval_status) updates.approval_status = sourceData.approval_status;
          if (sourceData.is_broadcast_master !== undefined && sourceData.is_broadcast_master !== localTrack.is_broadcast_master) updates.is_broadcast_master = sourceData.is_broadcast_master;
          if (sourceData.fileVersion && sourceData.fileVersion !== localTrack.fileVersion) updates.fileVersion = sourceData.fileVersion;
          if (sourceData.qcVersion && sourceData.qcVersion !== localTrack.qcVersion) updates.qcVersion = sourceData.qcVersion;
          if (sourceData.mp3Files) updates.mp3Files = sourceData.mp3Files;
          if (sourceData.wavFiles) updates.wavFiles = sourceData.wavFiles;
          if (Object.keys(updates).length > 0) {
            const localRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'audiometa'), trackId);
            await updateDoc(localRef, updates);
          }
        } catch { /* best effort — source may not be accessible */ }
      }
    };
    refreshDeliveryTracks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, currentOrgId, firebase]);

  // Block tab close / nav while the deliver flow is mid-flight. The flow
  // includes PDF generation + Storage upload + Firestore writes; closing
  // partway would leave the delivery without an attached report.
  useEffect(() => {
    if (!sharing) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the message and show their generic dialog,
      // but returnValue must be set for the prompt to appear.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [sharing]);

  // Run the actual share — returned as a callback so the FTP modal can trigger it
  const runShareJob = async (): Promise<{ success: boolean; error?: string; deliveries?: any[] }> => {
    if (!job || !firebase || !currentOrgId) return { success: false, error: 'Not ready' };
    setSharing(true);
    setError(null);
    try {
      // Phase 1: build the pending station-id set and pass it as the
      // subset filter. Legacy safety: any pending station without an
      // id (should be none after ChaseListUploader backfill, but
      // defensively) gets excluded here — the persist path will give
      // it one on next save.
      const pendingIds = new Set<string>();
      for (const s of job.stations) {
        const status = (s as any).deliveryStatus || 'pending';
        if (status !== 'delivered' && (s as any).id) {
          pendingIds.add((s as any).id);
        }
      }
      const result = await firebase.shareJobWithBroadcasters(currentOrgId, jobId, job, pendingIds);
      setShareResult(result);
      // Don't manually append to deliveries here — the onSnapshot listener will
      // pick up the newly created docs automatically. Appending here caused
      // duplicate keys when the listener also fired.

      const jobRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'), jobId);
      const jobDoc = await getDoc(jobRef);
      if (jobDoc.exists()) {
        setJob({ id: jobDoc.id, ...jobDoc.data() } as JobData);
      }

      if (result.errors.length > 0) {
        setError(`Some deliveries failed: ${result.errors.join('; ')}`);
      }
      setSharing(false);
      return { success: true, deliveries: result.deliveries };
    } catch (err: any) {
      setSharing(false);
      setError(`Failed to share job: ${err.message}`);
      return { success: false, error: err.message };
    }
  };

  // [FTP-MIGRATION 2026-06-15] gatherFtpTargets used to enumerate every
  // broadcaster on the job that had FTP enabled and group their files for
  // a cross-org FTP push at delivery time. FTP is now triggered from the
  // Download Manager and only ever uploads to the *current org's own* FTP,
  // so this helper is no longer wired in. Kept commented for reference in
  // case the broadcaster-FTP delivery flow is revived.
  // const gatherFtpTargets = async (): Promise<FtpDeliveryTarget[]> => {
  //   if (!job || !firebase || !currentOrgId) return [];
  //   // Group files per broadcaster orgId (station org or media owner org)
  //   const groups: Record<string, { orgName: string; files: Record<string, string> }> = {};
  //   for (const station of job.stations as any[]) {
  //     if (!station.linkedTrackId) continue;
  //     const track = getTrackInfo(station.linkedTrackId);
  //     if (!track) continue;
  //     const trackOrgId = track.sourceOrgId || currentOrgId;
  //     const wavFiles = track.wavFiles || [];
  //     const mp3Files = track.mp3Files || [];
  //     const files: any[] = wavFiles.length > 0 ? [wavFiles[0]] : (mp3Files.length > 0 ? [mp3Files[0]] : []);
  //     const candidates: { orgId: string; orgName: string }[] = [];
  //     if (station.stationOrgId) candidates.push({ orgId: station.stationOrgId, orgName: station.stationOrgName || station.station || '' });
  //     if (station.mediaOwnerOrgId) candidates.push({ orgId: station.mediaOwnerOrgId, orgName: station.mediaOwnerOrgName || station.mediaOwner || '' });
  //     for (const c of candidates) {
  //       if (!groups[c.orgId]) groups[c.orgId] = { orgName: c.orgName, files: {} };
  //       const g = groups[c.orgId];
  //       for (const f of files) {
  //         if (!f?.storageRef) continue;
  //         const storagePath = f.storageRef.includes('/')
  //           ? f.storageRef
  //           : `audio/${trackOrgId}/${station.linkedTrackId}/${f.storageRef}`;
  //         g.files[storagePath] = f.fileName || storagePath.split('/').pop();
  //       }
  //     }
  //   }
  //   const targets: FtpDeliveryTarget[] = [];
  //   for (const orgId of Object.keys(groups)) {
  //     const g = groups[orgId];
  //     try {
  //       const orgSnap = await getDoc(doc(firebase.db, 'organizations', orgId));
  //       const orgData = orgSnap.exists() ? (orgSnap.data() as any) : null;
  //       if (!orgData?.ftpEnabled) continue;
  //       const files = Object.keys(g.files).map(storagePath => ({ storagePath, remoteName: g.files[storagePath] }));
  //       if (files.length === 0) continue;
  //       targets.push({ broadcasterOrgId: orgId, broadcasterOrgName: orgData.name || g.orgName, files });
  //     } catch (err) {
  //       console.error('[JobDetail] gatherFtpTargets failed for', orgId, err);
  //     }
  //   }
  //   return targets;
  // };

  // Share job with broadcasters — entry point from the Deliver button
  // Publish Updates — push station-field edits made since the last
  // delivery into every broadcaster that received this campaign. Fires
  // when `hasPendingUpdates: true` on a delivered campaign. Distinct
  // from Deliver — no new delivery record, no notification, no data
  // duplication; it patches the existing snapshots in place. The
  // cascade logic lives on Firebase.ts::cascadeUpdateDeliveredStations
  // (added in Phase 5c). This function is the UI-side coordinator.
  const handlePublishUpdates = async () => {
    if (!job || !firebase || !currentOrgId || publishingUpdates) return;
    // Route the write upstream to the source when we're viewing a copy —
    // same pattern as handleShareJob / handleAutoMatch. The source is
    // the single source of truth for the patch fan-out.
    const isCopy = !!(job as any).isCampaignCopy;
    const writeOrgId: string = isCopy ? ((job as any).sourceOrgId || currentOrgId) : currentOrgId;
    const writeJobId: string = isCopy ? ((job as any).sourceJobId || jobId) : jobId;

    setPublishingUpdates(true);
    try {
      const res = await firebase.cascadeUpdateDeliveredStations(writeOrgId, writeJobId);
      console.log('[handlePublishUpdates] result:', res);
      if (res.errors && res.errors.length > 0) {
        console.warn('[handlePublishUpdates] non-fatal errors:', res.errors);
      }
      const summary = res.broadcasterCopiesUpdated > 0
        ? `Published updates to ${res.broadcasterCopiesUpdated} broadcaster${res.broadcasterCopiesUpdated === 1 ? '' : 's'} (${res.stationsPatched} station edit${res.stationsPatched === 1 ? '' : 's'}).`
        : 'No pending updates found — cleared the flag.';
      setMatchResult(summary);
      setTimeout(() => setMatchResult(null), 8000);
      // Local state refresh — clear the flag so the CTA flips back
      // without needing the live subscription to fire first.
      setJob(prev => prev ? { ...prev, hasPendingUpdates: false } : null);
    } catch (err: any) {
      console.error('[handlePublishUpdates] failed:', err);
      setError(`Publish Updates failed: ${err?.message || err}`);
    } finally {
      setPublishingUpdates(false);
    }
  };

  const handleShareJob = async () => {
    if (!job || !firebase || !currentOrgId || sharing) return;
    // Phase 1: the delivery gate is now driven by pendingCount rather
    // than by the whole-job hasActiveDelivery flag. We still bail if
    // there's genuinely nothing pending to send.
    const pending = job.stations.filter((s: any) => (s.deliveryStatus || 'pending') !== 'delivered');
    if (pending.length === 0) return;

    // Check all pending stations are linked. Live-reads count as
    // unlinked here to match the pre-Phase-1 confirm-dialog wording;
    // the actual delivery still includes live-reads correctly further
    // down in shareJobWithBroadcasters.
    const unlinked = pending.filter((s: any) => !s.linkedTrackId);
    if (unlinked.length > 0) {
      if (!window.confirm(
        `${unlinked.length} station${unlinked.length > 1 ? 's are' : ' is'} not linked to a track. ` +
        `Only linked stations will be delivered. Continue?`
      )) return;
    }

    // Language-acceptance check: cross-reference each linked station's spot
    // language against the broadcaster's `acceptedLanguages` whitelist. Surface
    // any mismatches in a confirm dialog so the user can override or cancel.
    {
      // Language-acceptance check scoped to the PENDING subset — the
      // stations we're about to ship. Already-delivered stations were
      // vetted at their own delivery time and are irrelevant here.
      const linked = pending.filter((s: any) => s.linkedTrackId);
      const orgIds = Array.from(
        new Set(linked.map((s: any) => s.stationOrgId).filter(Boolean) as string[]),
      );
      if (orgIds.length > 0) {
        try {
          // Resolve from the global broadcaster cache instead of querying
          // Firestore. The cache is loaded once at app boot and contains every
          // /broadcaster entry, so we just filter client-side by linkedOrgId.
          const broadcasterByOrgId = new Map<string, any>();
          const wantedIds = new Set(orgIds);
          for (const b of cachedBroadcasters) {
            const linkedOrgId = (b as any).linkedOrgId;
            if (linkedOrgId && wantedIds.has(linkedOrgId)) {
              broadcasterByOrgId.set(linkedOrgId, b);
            }
          }

          const violations: { station: string; spotLangs: string[]; accepted: string[] }[] = [];
          for (const station of linked) {
            if (!station.stationOrgId) continue;
            const bcaster = broadcasterByOrgId.get(station.stationOrgId);
            if (!bcaster) continue;
            const accepted: string[] = Array.isArray(bcaster.acceptedLanguages)
              ? bcaster.acceptedLanguages
              : [];
            if (accepted.length === 0) continue; // no restriction
            const spotLangs: string[] = (station.languageOptions || [])
              .map((o: any) => o.value)
              .filter(Boolean);
            if (spotLangs.length === 0) continue; // unknown language — skip
            const overlap = spotLangs.some((v) => accepted.includes(v));
            if (!overlap) {
              violations.push({
                station: station.station,
                spotLangs,
                accepted,
              });
            }
          }

          if (violations.length > 0) {
            const lines = violations
              .map(
                (v) =>
                  `  • ${v.station} — sending [${v.spotLangs.join(', ')}], accepts [${v.accepted.join(', ')}]`,
              )
              .join('\n');
            if (
              !window.confirm(
                `${violations.length} broadcaster${violations.length > 1 ? 's do' : ' does'} not accept the spot's language:\n\n${lines}\n\nContinue delivery anyway (override)?`,
              )
            ) {
              return;
            }
          }
        } catch (err) {
          console.warn('Language-acceptance check failed; proceeding:', err);
        }
      }
    }

    // [FTP-MIGRATION 2026-06-15] The pre-delivery FTP probe + modal trigger
    // were removed here. FTP now happens via the Download Manager (own org's
    // FTP only). Delivery proceeds straight to the in-app share.
    // setFtpChecking(true);
    // const targets = await gatherFtpTargets();
    // setFtpChecking(false);
    // if (targets.length > 0) {
    //   setFtpTargets(targets);
    //   setFtpModalOpen(true);
    //   return;
    // }

    // If the user opted this campaign into Delivery FTP for a client not yet on
    // the list, add it BEFORE creating deliveries so the FTP trigger sends this
    // one (and future campaigns for the client). Best-effort — never blocks.
    try {
      await ftpControlRef.current?.onBeforeDeliver?.();
    } catch (err) {
      console.warn('[handleShareJob] FTP onBeforeDeliver failed (continuing):', err);
    }

    await runShareJob();
  };

  // Batch QC — run QC on all linked tracks that need it
  const handleBatchQC = async () => {
    if (!job || !firebase || !currentOrgId) return;

    // Gather unique linked tracks that need QC
    const trackIds = new Set<string>();
    for (const station of job.stations) {
      if (station.linkedTrackId) {
        const track = getTrackInfo(station.linkedTrackId);
        if (track && track.qc_status !== 'pass' && track.qc_status !== 'testing') {
          trackIds.add(station.linkedTrackId);
        }
      }
    }

    const trackList = Array.from(trackIds);
    if (trackList.length === 0) {
      setMatchResult('All linked tracks have already passed QC.');
      setTimeout(() => setMatchResult(null), 5000);
      return;
    }

    setBatchQCing(true);
    setBatchQCProgress({ current: 0, total: trackList.length, results: '' });

    // Load org's default QC profile
    let profileId = 'ebu_r128';
    let profileData: any = null;
    try {
      const settingsRef = doc(firebase.db, `organizations/${currentOrgId}/settings/qc`);
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists() && settingsDoc.data()?.defaultProfile) {
        profileId = settingsDoc.data().defaultProfile;
      }
      // Load full profile data
      const profileRef = doc(firebase.db, 'qcProfiles', profileId);
      const profileDoc = await getDoc(profileRef);
      if (profileDoc.exists()) {
        profileData = profileDoc.data();
      }
    } catch { /* use default */ }

    const auth = getAuth();
    const user = auth.currentUser;
    let passed = 0;
    let failed = 0;

    for (let i = 0; i < trackList.length; i++) {
      const trackId = trackList[i];
      const track = getTrackInfo(trackId);
      if (!track) continue;

      setBatchQCProgress(prev => ({ ...prev, current: i + 1 }));

      try {
        // Get WAV file
        const wavFile = track.wavFiles?.[0];
        if (!wavFile?.downloadUrl) { failed++; continue; }

        // Determine where to write QC records — always to source org for shared history
        const isCrossOrg = track.sourceOrgId && track.sourceOrgId !== currentOrgId;
        const qcOrgId = isCrossOrg ? track.sourceOrgId : currentOrgId;
        const qcRef = collection(firebase.db, getOrgCollectionPath(qcOrgId, 'audiometa'), trackId, 'qcRecords');
        const qcData: any = {
          status: 'testing',
          profileId,
          profileName: profileData?.name || profileId,
          fileVersion: track.fileVersion || 1,
          qcTestNumber: 1,
          qcVersion: track.qcVersion || 0,
          fileName: wavFile.storageRef || wavFile.fileName,
          submittedAt: Date.now(),
          submittedBy: user?.uid || '',
          submittedByName: user?.displayName || user?.email || '',
          ranByOrgId: currentOrgId,
          ranByOrgName: currentOrgName || '',
        };
        const docRef = await addDoc(qcRef, qcData);

        // Set track to testing (local copy)
        const trackRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'audiometa'), trackId);
        await updateDoc(trackRef, { qc_status: 'testing' });
        // Also set source org track to testing
        if (isCrossOrg) {
          try {
            const sourceRef = doc(firebase.db, getOrgCollectionPath(track.sourceOrgId, 'audiometa'), trackId);
            await updateDoc(sourceRef, { qc_status: 'testing' });
          } catch { /* best effort */ }
        }

        // Fetch WAV and send to QC service
        const wavResponse = await fetch(wavFile.downloadUrl);
        const wavBlob = await wavResponse.blob();
        const formData = new FormData();
        formData.append('file', wavBlob, wavFile.storageRef || wavFile.fileName);
        if (profileData) {
          formData.append('profile_data', JSON.stringify(profileData));
        } else {
          formData.append('profile', profileId);
        }

        const response = await fetch(`${QC_SERVICE_URL}/analyze`, { method: 'POST', body: formData });
        if (!response.ok) { failed++; continue; }

        const report = await response.json();

        // Update QC record with results (written to source org's subcollection)
        await updateDoc(doc(qcRef, docRef.id), {
          status: report.passed ? 'pass' : 'fail',
          completedAt: Date.now(),
          summary: report.summary,
          checks: report.checks,
          loudness: report.loudness,
          audioFormat: report.audio_format,
          clippingEvents: report.clipping_events,
        });

        // Update track status and file entries' qcVersion
        const qcTestNumber = qcData.qcTestNumber;
        const trackUpdate: any = {
          qc_status: report.passed ? 'pass' : 'fail',
          qcVersion: qcTestNumber,
        };
        // Update qcVersion and fileName on file entries for this file version
        const fileVersion = track.fileVersion || 1;
        const updateFiles = (files: any[]) =>
          files.map((f: any) => {
            if ((f.version || 1) !== fileVersion) return f;
            const updated = { ...f, qcVersion: qcTestNumber };
            if (f.fileName) {
              updated.fileName = f.fileName.replace(/_qc\d+/, `_qc${qcTestNumber}`);
            }
            return updated;
          });
        if (track.mp3Files) trackUpdate.mp3Files = updateFiles(track.mp3Files);
        if (track.wavFiles) trackUpdate.wavFiles = updateFiles(track.wavFiles);

        if (report.passed) {
          trackUpdate.is_broadcast_master = true;
          trackUpdate.broadcast_master_file = {
            storageRef: wavFile.storageRef || wavFile.fileName,
            qcPassedAt: Date.now(),
            profileId,
          };
        }
        await updateDoc(trackRef, trackUpdate);

        // Sync QC status to master catalogue
        try {
          await firebase.syncMasterCatalogue(currentOrgId, trackId, {
            qc_status: report.passed ? 'pass' : 'fail',
            is_broadcast_master: report.passed,
          });
        } catch { /* best effort */ }

        // Sync QC status to source org's audiometa (cross-org tracks)
        if (isCrossOrg) {
          try {
            const sourceRef = doc(firebase.db, getOrgCollectionPath(track.sourceOrgId, 'audiometa'), trackId);
            await updateDoc(sourceRef, trackUpdate);
          } catch { /* best effort */ }
        }

        if (report.passed) passed++; else failed++;
      } catch (err) {
        console.error(`Batch QC error for ${trackId}:`, err);
        failed++;
      }
    }

    setBatchQCing(false);
    setMatchResult(`QC Complete: ${passed} passed, ${failed} failed out of ${trackList.length} tracks.`);
    setTimeout(() => setMatchResult(null), 10000);
  };

  // Batch Review — open modal to select target, then share all
  const handleBatchReviewOpen = () => {
    if (!job) return;
    // Check if there are tracks to review AND collect the candidates so we
    // can run the QC-before-Review gate against them in one pass.
    const seenIds = new Set<string>();
    const unreviewedTracks: any[] = [];
    for (const station of job.stations) {
      if (station.linkedTrackId && !seenIds.has(station.linkedTrackId)) {
        seenIds.add(station.linkedTrackId);
        const track = getTrackInfo(station.linkedTrackId);
        if (track && (!track.approval_status || track.approval_status === 'unshared' || track.approval_status === 'rejected')) {
          unreviewedTracks.push(track);
        }
      }
    }
    if (unreviewedTracks.length === 0) {
      setMatchResult('All linked tracks have already been shared for review.');
      setTimeout(() => setMatchResult(null), 5000);
      return;
    }
    // QC-before-Review gate. Refuses to open the share modal if any linked
    // track hasn't passed QC and the org rule is on (default). The toast
    // names the failing tracks so the user knows where to run QC.
    const gate = canShareBatchForReview(unreviewedTracks, { bypassQcBeforeReview });
    if (!gate.ok) {
      showWarning(gate.reason || 'Some spots haven\'t passed QC yet.');
      return;
    }
    setBatchReviewTarget(null);
    setBatchReviewTargetSearch('');
    setBatchReviewNotes('');
    setBatchReviewLink(null);
    setShowBatchReviewModal(true);
  };

  // Search share targets for batch review
  useEffect(() => {
    if (!showBatchReviewModal || !firebase || !batchReviewTargetSearch.trim()) {
      setBatchReviewTargetOptions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const results = await firebase.searchShareTargets(batchReviewTargetSearch);
        setBatchReviewTargetOptions(results);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timeout);
  }, [batchReviewTargetSearch, showBatchReviewModal, firebase]);

  // Execute batch review
  const handleBatchReviewSubmit = async () => {
    if (!job || !firebase || !currentOrgId) return;

    // Group station rows by their linkedTrackId. Every track being sent for
    // review gets a snapshot of "the stations this spot is flighting on"
    // so reviewers see the campaign context. Library-side reviews (which
    // don't come through this path) don't get stations — that's the
    // deliberate Option-A tradeoff.
    const stationsByTrack = new Map<string, Array<{ station: string; mediaOwner: string; flightingCode: string }>>();
    for (const s of job.stations) {
      if (!s.linkedTrackId) continue;
      const arr = stationsByTrack.get(s.linkedTrackId) || [];
      arr.push({
        station: (s as any).station || '',
        mediaOwner: (s as any).mediaOwner || '',
        flightingCode: (s as any).flightingCode || '',
      });
      stationsByTrack.set(s.linkedTrackId, arr);
    }

    const tracksToReview: any[] = [];
    const seenIds = new Set<string>();
    for (const station of job.stations) {
      if (station.linkedTrackId && !seenIds.has(station.linkedTrackId)) {
        seenIds.add(station.linkedTrackId);
        const track = getTrackInfo(station.linkedTrackId);
        if (track && (!track.approval_status || track.approval_status === 'unshared' || track.approval_status === 'rejected')) {
          tracksToReview.push({
            trackId: station.linkedTrackId,
            trackName: track.trackName || '',
            mp3Files: track.mp3Files || [],
            wavFiles: track.wavFiles || [],
            defaultMp3Id: track.defaultMp3Id || null,
            language: track.language || [],
            client: track.client || null,
            agency: track.agency || null,
            tags: track.tags || [],
            fileVersion: track.fileVersion || 1,
            sourceOrgId: track.sourceOrgId || null,
            // Track description (originated from chase-list description, lives
            // on audiometa.notes). Snapshot here so reviewers can read it
            // alongside the audio.
            notes: (track as any).notes || '',
            // Stations this spot flights on (snapshot). Undefined for
            // library-originated reviews — those go through createReviewRequest.
            stations: stationsByTrack.get(station.linkedTrackId) || [],
          });
        }
      }
    }

    if (tracksToReview.length === 0) return;

    setBatchReviewing(true);
    setBatchReviewProgress({ current: 0, total: tracksToReview.length });

    let sourceOrgName = '';
    try {
      const orgDoc = await getDoc(doc(firebase.db, 'organizations', currentOrgId));
      sourceOrgName = orgDoc.data()?.name || '';
    } catch { /* ignore */ }

    try {
      const batchCode = generateClaimCode();

      await firebase.createBatchReviewRequest(currentOrgId, tracksToReview, {
        batchReviewCode: batchCode,
        shareTarget: batchReviewTarget,
        notes: batchReviewNotes,
        sourceOrgName,
        campaign: job.campaign || '',
      });

      const link = `${window.location.origin}/review/${batchCode}`;
      setBatchReviewLink(link);

      // Save review code to job for later access
      const jobRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'), jobId);
      await updateDoc(jobRef, {
        batchReviewCode: batchCode,
        batchReviewLink: link,
        reviewSharedAt: Date.now(),
        reviewSharedTo: batchReviewTarget?.name || 'External link',
      });
      setJob(prev => prev ? { ...prev, batchReviewCode: batchCode, batchReviewLink: link, reviewSharedTo: batchReviewTarget?.name || 'External link' } as any : null);

      // Source org sync is now handled inside createBatchReviewRequest

      setMatchResult(`${tracksToReview.length} spots shared for review.`);
      setTimeout(() => setMatchResult(null), 10000);
    } catch (err: any) {
      setError(`Batch review failed: ${err.message}`);
    }

    setBatchReviewing(false);
  };

  // Auto-match flighting codes to existing tracks
  const handleAutoMatch = async () => {
    if (!job || !firebase || !currentOrgId) return;

    setMatching(true);
    const isMasterOrg = useOrganizationStore.getState().isMasterOrg;

    // Where the auto-match writes land. If this job is a campaign copy,
    // writes go UPSTREAM to the source's job doc; from there, syncCampaignCopies
    // fans back out to every copy (including the current org's copy).
    // For source jobs, writeOrgId === currentOrgId and writeJobId === jobId.
    const isCopy = !!(job as any).isCampaignCopy;
    const writeOrgId: string = isCopy ? ((job as any).sourceOrgId || currentOrgId) : currentOrgId;
    const writeJobId: string = isCopy ? ((job as any).sourceJobId || jobId) : jobId;

    let updatedStations: any[];
    // Map trackId → the org that ACTUALLY OWNS the audio (its uploader).
    // Used later to route trackUpdate writes to the right audiometa doc.
    const audioOwnerMap: Record<string, string> = {};

    if (isMasterOrg) {
      // Master org: search masterCatalogue for cross-org matches. The
      // campaign creator's convenience copy is now written uniformly by
      // syncTrackToCampaignOrgs below (via campaignSourceOrgId: writeOrgId).
      const catalogueRef = collection(firebase.db, 'masterCatalogue');
      const catalogueSnapshot = await getDocs(catalogueRef);
      const catalogueTracks = catalogueSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // TEMP diagnostic — matches the log added to the non-master
      // branch below so we can pinpoint the source-org auto-match bug
      // regardless of which path fires.
      console.log('[handleAutoMatch] MASTER path', {
        catalogueTrackCount: catalogueTracks.length,
        catalogueTrackNames: catalogueTracks.map((t: any) => ({ id: t.id, trackName: t.trackName })).slice(0, 50),
        stationFlightingCodes: job.stations.filter(s => !s.linkedTrackId && s.flightingCode && s.flightingCode !== 'TBA' && !(s as any).isLiveRead).map(s => s.flightingCode),
      });

      updatedStations = [];
      for (const station of job.stations) {
        // Skip stations that already have a VALID link, plus empty/TBA
        // codes and Live Reads. A dangling linkedTrackId (points to a
        // deleted track) falls through so this pass can re-resolve it
        // against the current catalogue.
        if (hasValidLink(station) || !station.flightingCode || station.flightingCode === 'TBA' || (station as any).isLiveRead) {
          updatedStations.push(station);
          continue;
        }

        const matched: any = catalogueTracks.find((t: any) =>
          t.trackName && station.flightingCode &&
          t.trackName.trim().toLowerCase() === station.flightingCode.trim().toLowerCase()
        );

        if (matched) {
          // The masterCatalogue entry always carries sourceOrgId (the artist
          // that published it). Fall back to the current org as a last
          // resort so downstream writes still land somewhere sensible.
          audioOwnerMap[matched.id] = matched.sourceOrgId || currentOrgId;
          updatedStations.push({ ...station, linkedTrackId: matched.id });
        } else {
          console.log(`[handleAutoMatch] MASTER: no match for "${station.flightingCode}"`);
          updatedStations.push(station);
        }
      }
    } else {
      // Non-master: multi-org search across every org tagged on this campaign.
      // Scope: current + source (if copy) + studio tag + agency tag. Uses
      // /audiometa read permission which is already open to authenticated
      // users. Merges the results, preferring the source-of-audio for
      // duplicates (an org's own upload beats a copy of the same trackId).
      const searchOrgIds = new Set<string>([currentOrgId]);
      const sourceOrgFromJob = (job as any).sourceOrgId;
      if (sourceOrgFromJob) searchOrgIds.add(sourceOrgFromJob);
      const studioTag = (job as any).studioOrgId;
      if (studioTag) searchOrgIds.add(studioTag);
      const agencyTag = (job as any).agencyOrgId;
      if (agencyTag) searchOrgIds.add(agencyTag);

      // Pull audiometa from each org. Best-effort per-org so a single
      // unreadable org doesn't kill the whole match.
      const allTracks: any[] = [];
      for (const orgId of Array.from(searchOrgIds)) {
        try {
          const snap = await getDocs(collection(firebase.db, getOrgCollectionPath(orgId, 'audiometa')));
          snap.docs.forEach((d) => {
            const data = d.data() as any;
            // Prefer the ultimate audio owner (present as sourceOrgId on a
            // copy) — otherwise the audio was uploaded to this org directly.
            const audioOwnerOrgId = data.sourceOrgId || orgId;
            allTracks.push({ id: d.id, foundInOrgId: orgId, audioOwnerOrgId, ...data });
          });
        } catch (err) {
          console.warn(`Auto-match: could not read audiometa from org ${orgId}:`, err);
        }
      }

      // Dedupe by trackId, preferring the doc found in its OWN audio-owner
      // org (i.e. the canonical source, not a downstream copy).
      const bestByTrackId = new Map<string, any>();
      for (const t of allTracks) {
        const existing = bestByTrackId.get(t.id);
        const tIsCanonical = t.foundInOrgId === t.audioOwnerOrgId;
        const existingIsCanonical = existing && existing.foundInOrgId === existing.audioOwnerOrgId;
        if (!existing || (tIsCanonical && !existingIsCanonical)) {
          bestByTrackId.set(t.id, t);
        }
      }
      const dedupedTracks = Array.from(bestByTrackId.values());

      // TEMP diagnostic — remove once the "auto-match doesn't find
      // just-unlinked track" bug is understood.
      console.log('[handleAutoMatch] search context', {
        searchOrgIds: Array.from(searchOrgIds),
        allTracksCount: allTracks.length,
        dedupedTracksCount: dedupedTracks.length,
        dedupedTrackNames: dedupedTracks.map((t: any) => ({ id: t.id, trackName: t.trackName, foundInOrgId: t.foundInOrgId })),
        stationFlightingCodes: job.stations.filter(s => !s.linkedTrackId && s.flightingCode && s.flightingCode !== 'TBA' && !(s as any).isLiveRead).map(s => s.flightingCode),
      });

      updatedStations = job.stations.map(station => {
        // Skip stations with a VALID link — a dangling linkedTrackId
        // (points to a deleted track) falls through so this pass can
        // re-resolve it against the current catalogue.
        if (hasValidLink(station)) return station;
        if (!station.flightingCode || station.flightingCode === 'TBA') return station;
        // Live Reads have no audio — leave the flag intact and skip matching.
        if ((station as any).isLiveRead) return station;
        const matchedTrack = dedupedTracks.find((t: any) =>
          t.trackName && station.flightingCode &&
          t.trackName.trim().toLowerCase() === station.flightingCode.trim().toLowerCase()
        );
        if (matchedTrack) {
          audioOwnerMap[matchedTrack.id] = matchedTrack.audioOwnerOrgId;
          return { ...station, linkedTrackId: matchedTrack.id };
        } else {
          console.log(`[handleAutoMatch] no match for flightingCode="${station.flightingCode}" — closest by name:`,
            dedupedTracks.filter((t: any) => t.trackName && station.flightingCode &&
              t.trackName.toLowerCase().includes(station.flightingCode.substring(0, 5).toLowerCase())
            ).slice(0, 3).map((t: any) => t.trackName));
        }
        return station;
      });
    }

    const linkedCount = updatedStations.filter(s => hasValidLink(s)).length;

    try {
      // Write to the SOURCE job doc (writeOrgId/writeJobId) — upstream when
      // this is a copy, local when this is the source. syncCampaignCopies
      // then fans out to every downstream copy.
      const jobRef = doc(firebase.db, getOrgCollectionPath(writeOrgId, 'jobs'), writeJobId);
      const wasDelivered = (job as any).status === 'delivered';
      // Detect NET NEW links landing on stations that were previously
      // delivered — that's the case where the broadcaster's delivered
      // snapshot now points at a different (or missing → present)
      // trackId. On a delivered campaign, that's a stale-snapshot
      // event → set hasPendingUpdates so the CTA becomes "Publish
      // Updates". A fresh auto-match on an all-unlinked delivered
      // campaign (rare) also qualifies.
      let anyRelinkOnDeliveredStation = false;
      if (wasDelivered) {
        for (let i = 0; i < updatedStations.length; i++) {
          const before = job.stations[i];
          const after = updatedStations[i];
          if (!before || !after) continue;
          if (before.linkedTrackId !== after.linkedTrackId && (before as any).deliveryStatus === 'delivered') {
            anyRelinkOnDeliveredStation = true;
            break;
          }
        }
      }
      const jobUpdate: any = {
        stations: updatedStations,
        linkedCount,
        // Snapshot model — delivered stays delivered on auto-match.
        // Draft becomes draft (unchanged).
        status: wasDelivered ? 'delivered' : 'draft',
      };
      if (anyRelinkOnDeliveredStation) jobUpdate.hasPendingUpdates = true;
      await updateDoc(jobRef, jobUpdate);

      // Fan out to studio + agency copies. `syncCampaignCopies` reads the
      // studio/agency tags from the payload and writes copies (self-skips
      // the source). The current org's copy — if this run was on a copy —
      // gets refreshed by the same fan-out.
      try {
        await firebase.syncCampaignCopies(writeOrgId, writeJobId, {
          ...(job as any),
          ...jobUpdate,
        });
      } catch (err) {
        console.warn('syncCampaignCopies after auto-match failed (best effort):', err);
      }

      // Resolve — or auto-create — the campaign's studio + agency in
      // their global lookup collections BEFORE the per-track loop, so
      // the same {id,label,value} is reused for every track. Auto-
      // create-if-missing keeps duplicate lookup entries from piling
      // up (matches by linkedOrgId first, then by label) and mirrors
      // what the AddAudioForm's "create if not exists" prompt would
      // do if the operator picked these values manually.
      let resolvedStudio: { id: string; label: string; value: string } | null = null;
      let resolvedAgency: { id: string; label: string; value: string } | null = null;
      const jobStudioName = ((job as any).studioName || '').toString().trim();
      const jobAgencyName = ((job as any).agencyName || '').toString().trim();
      if (jobStudioName) {
        try {
          resolvedStudio = await firebase.resolveOrCreateLookupEntry('artists-global', {
            orgId: (job as any).studioOrgId || null,
            name: jobStudioName,
          });
        } catch (err) {
          console.warn('[handleAutoMatch] resolveOrCreateLookupEntry(studio) failed:', err);
        }
      }
      if (jobAgencyName) {
        try {
          resolvedAgency = await firebase.resolveOrCreateLookupEntry('agency', {
            orgId: (job as any).agencyOrgId || null,
            name: jobAgencyName,
          });
        } catch (err) {
          console.warn('[handleAutoMatch] resolveOrCreateLookupEntry(agency) failed:', err);
        }
      }

      // Per-track updates. Everything about the track (jobId back-reference,
      // campaign name, description → notes) lives on the AUDIO OWNER's
      // audiometa doc. Copies (studio/agency/creator) get the update via
      // syncTrackToCampaignOrgs.
      for (const station of updatedStations) {
        if (!station.linkedTrackId) continue;
        const audioOwnerOrgId: string =
          audioOwnerMap[station.linkedTrackId]
          || getTrackInfo(station.linkedTrackId)?.sourceOrgId
          || currentOrgId;

        const trackUpdate: any = {
          jobId: writeJobId,
          jobCampaign: job.campaign,
          jobTitle: job.campaign,
        };
        if (job.client && typeof job.client === 'object' && (job.client as any).label) {
          trackUpdate.client = job.client;
        }
        // Propagate the resolved campaign studio + agency onto the
        // audiometa doc so the Library table's Studio / Agency columns
        // populate and the fields flow to studio / agency campaign
        // copies below. Overwrites any existing value (same "campaign
        // wins" rule the `client` handling above uses).
        if (resolvedStudio) trackUpdate.studio = resolvedStudio;
        if (resolvedAgency) trackUpdate.agency = resolvedAgency;
        const stationLang = (station as any).languageOptions;
        if (Array.isArray(stationLang) && stationLang.length > 0) {
          trackUpdate.language = stationLang;
        }
        const stationDescription = (station as any).description;
        if (typeof stationDescription === 'string' && stationDescription.trim()) {
          trackUpdate.notes = stationDescription;
        }

        // Source-of-audio write: the canonical trackUpdate lands here.
        try {
          const audioOwnerRef = doc(
            firebase.db,
            getOrgCollectionPath(audioOwnerOrgId, 'audiometa'),
            station.linkedTrackId,
          );
          await updateDoc(audioOwnerRef, trackUpdate);
        } catch { /* best effort — cross-org permission may block */ }

        // Master catalogue + review-copy sync always point at the audio owner.
        try { await firebase.syncMasterCatalogue(audioOwnerOrgId, station.linkedTrackId, trackUpdate); } catch { /* best effort */ }
        try { await firebase.syncReviewTrackCopies(audioOwnerOrgId, station.linkedTrackId, trackUpdate); } catch { /* best effort */ }

        // Fan the track into studio + agency + campaign creator libraries.
        // Pulls the FULL source track first so the copies are complete
        // mirrors (including files), then overlays trackUpdate on top.
        try {
          let baseTrackData: any = null;
          if (audioOwnerOrgId === currentOrgId) {
            const local = getTrackInfo(station.linkedTrackId);
            if (local) baseTrackData = { ...local };
          } else {
            const srcRef = doc(
              firebase.db,
              getOrgCollectionPath(audioOwnerOrgId, 'audiometa'),
              station.linkedTrackId,
            );
            const srcSnap = await getDoc(srcRef);
            if (srcSnap.exists()) baseTrackData = srcSnap.data();
          }
          if (baseTrackData) {
            delete baseTrackData.isReviewTrack;
            delete baseTrackData.isDeliveryTrack;
            delete baseTrackData.isCampaignCopy;
            await firebase.syncTrackToCampaignOrgs(
              audioOwnerOrgId,
              station.linkedTrackId,
              { ...baseTrackData, ...trackUpdate },
              {
                studioOrgId: (job as any).studioOrgId || '',
                agencyOrgId: (job as any).agencyOrgId || '',
                campaignSourceOrgId: writeOrgId,
              },
            );
          }
        } catch (err) {
          console.warn('syncTrackToCampaignOrgs failed during auto-match (best effort):', err);
        }
      }

      const newStatus = linkedCount === updatedStations.length ? 'active' : 'draft';
      setJob(prev => prev ? { ...prev, stations: updatedStations, linkedCount, status: newStatus } : null);

      // History — one entry per auto-match run, with a link_added change
      // per newly linked station. Track names come from the local tracks
      // store where available.
      try {
        const linkChanges: CampaignChange[] = [];
        job.stations.forEach((prev, idx) => {
          const next = updatedStations[idx];
          if (!prev.linkedTrackId && next.linkedTrackId) {
            const info = getTrackInfo(next.linkedTrackId);
            linkChanges.push({
              kind: 'link_added',
              stationRef: next.flightingCode || '(no code)',
              broadcaster: next.station || null,
              mediaOwner: next.mediaOwner || null,
              trackId: next.linkedTrackId,
              trackName: info?.trackName || null,
            });
          }
        });
        if (linkChanges.length > 0) {
          await firebase.logCampaignChange(writeOrgId, writeJobId, {
            ...buildActor(),
            changes: linkChanges,
          });
        }
      } catch (err) {
        console.warn('logCampaignChange failed on auto-match (best effort):', err);
      }

      // Show match results
      const previousLinked = job.stations.filter(s => s.linkedTrackId).length;
      const newlyMatched = linkedCount - previousLinked;
      const unmatched = updatedStations.filter(s => !s.linkedTrackId && s.flightingCode && s.flightingCode !== 'TBA' && !(s as any).isLiveRead);
      if (newlyMatched > 0 && unmatched.length === 0) {
        setMatchResult('All stations matched successfully!');
      } else if (newlyMatched > 0) {
        setMatchResult(`${newlyMatched} track${newlyMatched > 1 ? 's' : ''} matched. ${unmatched.length} unmatched: ${unmatched.map(s => s.flightingCode).join(', ')}`);
      } else if (unmatched.length > 0) {
        setMatchResult(`No new matches found. Unmatched codes: ${unmatched.map(s => s.flightingCode).join(', ')}`);
      } else {
        setMatchResult('All stations are already matched.');
      }
      setTimeout(() => setMatchResult(null), 10000);
    } catch (err: any) {
      setError(`Matching failed: ${err.message}`);
    }
    setMatching(false);
  };

  // Unlink a track from a station. Mirrors the upstream-write rule used by
  // handleAutoMatch: if we're viewing a copy, the actual write goes to the
  // source job doc, then syncCampaignCopies fans back out to every copy
  // (including this one). After the sync, cleanupOrphanTrackCopies removes
  // any studio/agency/creator audiometa copies of the unlinked track that
  // aren't referenced by another campaign in that org — so libraries
  // don't accumulate orphan copies over time.
  const handleUnlink = async (stationIndex: number) => {
    if (!job || !firebase || !currentOrgId) return;

    const isCopy = !!(job as any).isCampaignCopy;
    const writeOrgId: string = isCopy ? ((job as any).sourceOrgId || currentOrgId) : currentOrgId;
    const writeJobId: string = isCopy ? ((job as any).sourceJobId || jobId) : jobId;

    // Capture the trackId BEFORE mutating stations — we need it to decide
    // which audiometa copies to consider for cleanup.
    const unlinkedTrackId: string | null | undefined = job.stations[stationIndex]?.linkedTrackId;

    const updatedStations = [...job.stations];
    updatedStations[stationIndex] = { ...updatedStations[stationIndex], linkedTrackId: null };
    const linkedCount = updatedStations.filter(s => hasValidLink(s)).length;

    try {
      const jobRef = doc(firebase.db, getOrgCollectionPath(writeOrgId, 'jobs'), writeJobId);
      // On a delivered campaign, unlinking a track means the snapshot
      // broadcasters received is now stale — flag it so the CTA
      // switches to "Publish Updates". Status stays 'delivered' (the
      // snapshot itself is still live). Draft campaigns don't need
      // the flag — they haven't been snapshotted yet.
      const wasDelivered = (job as any).status === 'delivered';
      const jobUpdate: any = { stations: updatedStations, linkedCount };
      if (wasDelivered) jobUpdate.hasPendingUpdates = true;
      await updateDoc(jobRef, jobUpdate);
      setJob(prev => prev ? { ...prev, stations: updatedStations, linkedCount, ...(wasDelivered ? { hasPendingUpdates: true } : {}) } : null);
      // Propagate the unlink to studio + agency campaign copies so their
      // view of the stations stays consistent with the source.
      try {
        await firebase.syncCampaignCopies(writeOrgId, writeJobId, {
          ...(job as any),
          ...jobUpdate,
        });
      } catch (err) {
        console.warn('syncCampaignCopies after unlink failed (best effort):', err);
      }
      // Clean up orphan audiometa copies. Only fires when we actually
      // removed a linked trackId. The helper checks each target org for
      // other campaigns still referencing the track before deleting.
      if (unlinkedTrackId) {
        // Audio owner: prefer the track's sourceOrgId (canonical) — fall back
        // to writeOrgId which for the source flow equals currentOrgId.
        const local = getTrackInfo(unlinkedTrackId);
        const audioOwnerOrgId: string = (local && local.sourceOrgId) || writeOrgId;
        // Candidates: every org that syncTrackToCampaignOrgs writes into.
        const targetOrgIds: (string | null | undefined)[] = [
          (job as any).studioOrgId,
          (job as any).agencyOrgId,
          writeOrgId, // campaign creator
        ];
        try {
          await firebase.cleanupOrphanTrackCopies(audioOwnerOrgId, unlinkedTrackId, targetOrgIds);
        } catch (err) {
          console.warn('cleanupOrphanTrackCopies failed after unlink (best effort):', err);
        }
        // History — one entry with a link_removed change.
        try {
          const unlinkedStation = job.stations[stationIndex];
          const info = getTrackInfo(unlinkedTrackId);
          await firebase.logCampaignChange(writeOrgId, writeJobId, {
            ...buildActor(),
            changes: [{
              kind: 'link_removed',
              stationRef: unlinkedStation?.flightingCode || '(no code)',
              broadcaster: unlinkedStation?.station || null,
              mediaOwner: unlinkedStation?.mediaOwner || null,
              trackId: unlinkedTrackId,
              trackName: info?.trackName || null,
            }],
          });
        } catch (err) {
          console.warn('logCampaignChange failed on unlink (best effort):', err);
        }
      }
    } catch (err: any) {
      setError(`Failed to unlink: ${err.message}`);
    }
  };

  // Get track info for a linked track
  const getTrackInfo = (trackId: string): any => {
    return tracks[trackId] || null;
  };

  // A station's link is "valid" only when its linkedTrackId points to
  // a track that still exists in the tracks store (real-time snapshot
  // of audiometa). A dangling ID — the track was deleted from the
  // library but the station still references it — is treated as
  // unlinked everywhere: it doesn't count toward linkedCount, it DOES
  // count as unmatched (so Auto-Match becomes available), and it
  // falls through the match loop as a candidate for re-matching.
  const hasValidLink = (station: any): boolean =>
    !!(station?.linkedTrackId && getTrackInfo(station.linkedTrackId));

  const getStatusBadge = (status: string) => {
    if (isReceivedJob) {
      switch (status) {
        case 'accepted': return <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.3)', color: '#50c878' }}>Accepted</Badge>;
        case 'delivered':
        default: return <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.3)', color: '#50c878' }}>Received</Badge>;
      }
    }
    switch (status) {
      case 'delivered': return <Badge style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}>Delivered</Badge>;
      // Legacy 'active' falls through to Draft (passive migration).
      default: return <Badge style={{ backgroundColor: 'rgba(255, 180, 50, 0.3)', color: '#ffb432' }}>Draft</Badge>;
    }
  };

  const getApprovalBadge = (track: any) => {
    if (!track) return null;
    switch (track.approval_status) {
      case 'approved': return <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.3)', color: '#50c878', fontSize: '0.65rem' }}>Approved</Badge>;
      case 'pending': return <Badge style={{ backgroundColor: 'rgba(255, 180, 50, 0.3)', color: '#ffb432', fontSize: '0.65rem' }}>Pending</Badge>;
      case 'rejected': return <Badge style={{ backgroundColor: 'rgba(255, 80, 80, 0.3)', color: '#ff5050', fontSize: '0.65rem' }}>Rejected</Badge>;
      default: return null;
    }
  };

  const getQCBadge = (track: any) => {
    if (!track) return null;
    switch (track.qc_status) {
      case 'pass': return <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.3)', color: '#50c878', fontSize: '0.65rem' }}>QC Pass</Badge>;
      case 'fail': return <Badge style={{ backgroundColor: 'rgba(255, 80, 80, 0.3)', color: '#ff5050', fontSize: '0.65rem' }}>QC Fail</Badge>;
      case 'testing': return <Badge style={{ backgroundColor: 'rgba(255, 180, 50, 0.3)', color: '#ffb432', fontSize: '0.65rem' }}>Testing</Badge>;
      default: return null;
    }
  };

  // Close actions menu on outside click
  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionsMenuOpen]);

  // Deliverable flighting codes for this campaign — the codes the FTP push
  // would produce (real flightingCode, else trackName; 'TBA' is a placeholder),
  // for stations that carry audio. Compared against the FTP sent-set to know
  // whether the campaign has already been sent to the FTP. Declared here (before
  // the early returns below) to keep hook order stable.
  const campaignFtpCodes = useMemo(() => {
    const set = new Set<string>();
    for (const d of deliveries as any[]) {
      for (const s of (Array.isArray(d.stations) ? d.stations : [])) {
        const hasAudio = (Array.isArray(s.mp3Files) && s.mp3Files.length > 0)
          || (Array.isArray(s.wavFiles) && s.wavFiles.length > 0);
        if (!hasAudio) continue;
        const real = s.flightingCode && String(s.flightingCode).trim().toUpperCase() !== 'TBA';
        const code = String((real ? s.flightingCode : (s.trackName || '')) || '').trim();
        if (code) set.add(code);
      }
    }
    return Array.from(set);
  }, [deliveries]);

  if (loading) {
    return (
      <PageContainer>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
          <Spinner style={{ color: 'var(--accent-1-color)' }} />
        </Container>
      </PageContainer>
    );
  }

  if (error || !job) {
    return (
      <PageContainer>
        <Container>
          <Card className="no-bg-card mt-4">
            <CardBody className="text-center">
              <p style={{ color: '#ff5050' }}>{error || 'Campaign not found'}</p>
              <Button className="btn-primary-custom" onClick={() => history.push(ROUTES.JOBS)}>
                <span style={{ position: 'relative', zIndex: 2 }}>Back to Campaigns</span>
              </Button>
            </CardBody>
          </Card>
        </Container>
      </PageContainer>
    );
  }

  // Uses `hasValidLink` (checks the linkedTrackId resolves to an
  // actual track in the live tracks store) not raw string truthiness —
  // so a station whose linkedTrackId points to a deleted-from-library
  // track counts as UNMATCHED, not linked. That's what lights up
  // Auto-Match again after a QC-fail delete/reupload.
  const linkedCount = job.stations.filter(s => hasValidLink(s)).length;
  // Live-reads have no audio to link but ARE valid campaign entries — they
  // count towards delivery readiness and are excluded from the unmatched
  // count so the "N unlinked" hint on the primary CTA reflects only rows
  // that still need matching / uploading.
  const liveReadCount = job.stations.filter(s => (s as any).isLiveRead).length;
  const readyCount = linkedCount + liveReadCount;
  const unmatchedCount = job.stations.filter(s => !hasValidLink(s) && !(s as any).isLiveRead).length;

  // PENDING SUBSET — stations that haven't been marked 'delivered' on
  // the source. Populated by shareJobWithBroadcasters after a
  // successful share (Phase 1). On a fresh campaign every station is
  // 'pending', so `pendingStations` = all stations = identical
  // behaviour to pre-Phase-1. On an edited-after-delivery campaign,
  // pendingStations is just the newly-added rows (or the ones that
  // got flipped back to 'pending' by rejectDelivery).
  const pendingStations = job.stations.filter((s: any) => (s.deliveryStatus || 'pending') !== 'delivered');
  const pendingCount = pendingStations.length;

  // Compute track statuses — SCOPED TO THE PENDING SUBSET. Already-
  // delivered stations don't block re-delivery of newly-added ones.
  // Live-reads inside the pending subset still count as "ready" but
  // don't need QC / review.
  const pendingLinkedTrackIds = Array.from(new Set(pendingStations.filter(s => s.linkedTrackId).map(s => s.linkedTrackId!)));
  const pendingLinkedTrackData = pendingLinkedTrackIds.map(id => getTrackInfo(id)).filter(Boolean);
  const qcPassedCount = pendingLinkedTrackData.filter((t: any) => t.qc_status === 'pass').length;
  const allQCPassed = pendingLinkedTrackData.length > 0 && qcPassedCount === pendingLinkedTrackData.length;
  const approvedCount = pendingLinkedTrackData.filter((t: any) => t.approval_status === 'approved').length;
  const allApproved = pendingLinkedTrackData.length > 0 && approvedCount === pendingLinkedTrackData.length;
  const needsQC = !bypassQc && pendingLinkedTrackData.some((t: any) => t.qc_status !== 'pass' && t.qc_status !== 'testing');
  const needsReview = !bypassReview && pendingLinkedTrackData.some((t: any) => !t.approval_status || t.approval_status === 'unshared' || t.approval_status === 'rejected');

  // Delivery-record view (whole-job scope) — kept for the historical
  // banner + "Re-Deliver" label semantics. Deliveries whose status is
  // 'deleted' (Phase 2 cascade — every station in the delivery was
  // removed from the source campaign) are treated as terminal, same
  // as 'rejected', so the CTA doesn't stay stuck on "Delivered".
  const isTerminalDeliveryStatus = (s: string | undefined) => s === 'rejected' || s === 'deleted';
  const hasActiveDelivery = deliveries.some((d: any) => !isTerminalDeliveryStatus(d.status));
  const allDeliveriesRejected = deliveries.length > 0 && deliveries.every((d: any) => isTerminalDeliveryStatus(d.status));
  const hasPriorDelivery = deliveries.length > 0;

  // Campaign client as a plain string (client can be a string or {label}).
  const campaignClient = ((typeof job?.client === 'object'
    ? (job?.client as any)?.label
    : job?.client) || '').toString().trim();
  // Partial-redeliver requires BOTH signals to be true:
  //   * a non-terminal delivery record exists (broadcaster side)
  //   * at least one station on the source job is actually flagged
  //     'delivered' (source side)
  // The second check closes a first-delivery race: right after clicking
  // Deliver, the deliveries subcollection listener fires (~200ms) BEFORE
  // shareJobWithBroadcasters finishes flipping the source stations to
  // 'delivered' + the local job re-fetches. Without this gate the
  // "N new stations added since the last delivery" banner briefly flashed
  // on during a first-time delivery.
  const hasDeliveredStations = job.stations.some((s: any) => (s as any).deliveryStatus === 'delivered');
  const isPartialRedeliver = hasActiveDelivery && pendingCount > 0 && hasDeliveredStations;

  // Bypass flags from org settings let master orgs deliver without these gates.
  // Banner near the Deliver button surfaces which overrides are active.
  // Live-read-only pending subsets have no audio to QC or review — treat
  // the gates as trivially met when there are literally no linked tracks
  // to check (the delivery-readiness pipeline still requires readyCount > 0
  // downstream, so an empty pending set can't slip through here).
  const noAudioToCheck = pendingLinkedTrackData.length === 0;
  const qcGateOk = noAudioToCheck || allQCPassed || bypassQc;
  const reviewGateOk = noAudioToCheck || allApproved || bypassReview;
  // Phase 1: delivery gate opens whenever there are pending stations
  // AND the QC / Review gates for that subset are met. Fresh campaigns
  // → pendingCount = total stations → same as pre-Phase-1. All-rejected
  // campaigns → rejectDelivery flipped stations back to 'pending' →
  // same as pre-Phase-1 re-deliver behaviour.
  const readyToDeliver = qcGateOk && reviewGateOk && pendingCount > 0;

  // Determine smart primary action
  const getPrimaryAction = () => {
    // Agency copies never get a primary CTA — their only affordance is
    // Edit Campaign, which lives in the actions menu. Gate up front so
    // the "no tracks linked yet" branch below doesn't hand them Upload
    // Spots / Auto-Match buttons they aren't allowed to use.
    if (isCampaignCopy && jobCopyRole === 'agency') return null;
    // Unlinked stations take priority — need to upload or re-match. Live
    // reads are excluded from `unmatchedCount` so an all-live-read campaign
    // skips straight to the delivery CTA.
    if (unmatchedCount > 0 && readyCount > 0) {
      return { label: `Auto-Match (${unmatchedCount} unlinked)`, icon: 'tim-icons icon-link-72', action: handleAutoMatch, className: 'btn-primary-custom', disabled: matching, spinning: matching, spinLabel: 'Matching...' };
    }
    if (readyCount === 0) {
      return [
        { label: 'Upload Spots', icon: 'tim-icons icon-cloud-upload-94', action: () => history.push(ROUTES.ADD_AUDIO, { fromJobId: jobId }), className: 'btn-success-custom' },
        { label: 'Auto-Match', icon: 'tim-icons icon-link-72', action: handleAutoMatch, className: 'btn-primary-custom', disabled: matching, spinning: matching, spinLabel: 'Matching...' },
      ];
    }
    // Creator copies: once linked, no further primary CTA — Deliver / QC /
    // Review are source-only.
    if (isCampaignCopy && jobCopyRole === 'creator') return null;
    // Phase 1 CTA labelling: distinguish the three states.
    //   * Fresh / never-delivered campaign — "Deliver"
    //   * All prior deliveries rejected — "Re-Deliver" (existing)
    //   * Partial: prior delivery exists AND new pending stations → "Deliver N New Stations"
    const deliverLabel = isPartialRedeliver
      ? `Deliver ${pendingCount} New Station${pendingCount === 1 ? '' : 's'}`
      : (allDeliveriesRejected ? 'Re-Deliver' : 'Deliver');
    if (readyToDeliver) return { label: deliverLabel, icon: 'tim-icons icon-send', action: handleShareJob, className: 'btn-primary-custom', disabled: sharing, spinning: sharing, spinLabel: 'Sharing...' };
    // Delivered + station-field edits landed since last publish →
    // offer "Publish Updates". Pushes the edits to broadcaster
    // delivery snapshots + copies + audiometa. Distinct from Deliver
    // — no new delivery record, no new notification, no duplication.
    if (hasActiveDelivery && pendingCount === 0 && (job as any).hasPendingUpdates) {
      return { label: 'Publish Updates', icon: 'tim-icons icon-refresh-01', action: handlePublishUpdates, className: 'btn-warning-custom', disabled: publishingUpdates, spinning: publishingUpdates, spinLabel: 'Publishing...', hint: 'Push field edits made since delivery to every broadcaster that received this campaign' };
    }
    // Fully-delivered with nothing pending — the old "Delivered" dead
    // button. Only shown when there are literally no more stations to
    // deliver AND at least one active delivery exists.
    if (hasActiveDelivery && pendingCount === 0) return { label: 'Delivered', icon: 'tim-icons icon-check-2', action: () => {}, className: 'btn-primary-custom', disabled: true, hint: 'All stations on this campaign have been delivered' };
    if (needsQC) return { label: `QC All`, icon: 'tim-icons icon-check-2', action: handleBatchQC, className: 'btn-info-custom', disabled: batchQCing, spinning: batchQCing, spinLabel: `QC ${batchQCProgress.current}/${batchQCProgress.total}` };
    if (needsReview) return { label: 'Review All', icon: 'tim-icons icon-shape-star', action: handleBatchReviewOpen, className: 'btn-warning-custom', disabled: batchReviewing, spinning: batchReviewing, spinLabel: `Review ${batchReviewProgress.current}/${batchReviewProgress.total}` };
    // Pending review/QC — show deliver button but disabled until all spots are ready
    const pendingHint = !qcGateOk && !reviewGateOk
      ? 'All spots must pass QC and be approved'
      : !qcGateOk
      ? 'All spots must pass QC'
      : 'All spots must be approved';
    return { label: deliverLabel, icon: 'tim-icons icon-send', action: handleShareJob, className: 'btn-primary-custom', disabled: true, hint: pendingHint };
  };

  const primaryAction = !isReceivedJob ? getPrimaryAction() : null;

  return (
    <div className="wrapper pt-3 pb-3">
      <Container fluid style={{ maxWidth: '1400px' }}>
        <Row>
          <Col>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <Button
                  color="link"
                  onClick={() => history.push(ROUTES.JOBS)}
                  style={{ color: 'rgba(255,255,255,0.5)', padding: 0, marginBottom: '0.5rem', fontSize: '0.85rem' }}
                >
                  &larr; Back to Campaigns
                </Button>
                <h2 className="text-uppercase mb-0" style={{ color: '#fff' }}>
                  {getCampaignName(job.campaign)}
                  <span style={{ marginLeft: '0.75rem' }}>{getStatusBadge(job.status)}</span>
                  {/* Updates-pending pill — visible on any delivered
                      campaign that has station-field edits waiting to
                      be published to broadcasters. Complements the
                      "Publish Updates" CTA on the primary action. */}
                  {job.status === 'delivered' && (job as any).hasPendingUpdates && (
                    <Badge style={{
                      marginLeft: '0.5rem',
                      backgroundColor: 'rgba(255, 180, 50, 0.2)',
                      color: '#ffb432',
                      fontSize: '0.65rem',
                      textTransform: 'none',
                      letterSpacing: 0,
                    }}>
                      updates pending
                    </Badge>
                  )}
                </h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginBottom: 0 }}>
                  {typeof job.client === 'object' ? (job.client as any)?.label : job.client || '--'} · {job.period || '--'} · {job.stationCount} stations
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                {isReceivedJob ? (
                  <>
                    <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.15)', color: '#50c878', fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}>
                      <i className="tim-icons icon-cloud-download-93 mr-1"></i>
                      Received from {job.sourceOrgName || 'studio'}
                    </Badge>
                    <Button
                      className="btn-success-custom"
                      onClick={() => setShowDownloadManager(true)}
                      disabled={linkedCount === 0}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                        <i className="tim-icons icon-cloud-download-93 mr-2"></i>Download Spots
                      </span>
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Smart primary action button(s) */}
                    {primaryAction && (Array.isArray(primaryAction) ? primaryAction : [primaryAction]).map((action: any, idx: number) => (
                      <Button
                        key={idx}
                        className={action.className}
                        onClick={action.action}
                        disabled={action.disabled}
                        title={action.hint || ''}
                        style={idx > 0 ? { marginLeft: '0.5rem' } : undefined}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>
                          {action.spinning ? (
                            <><Spinner size="sm" className="mr-1" /> {action.spinLabel}</>
                          ) : (
                            <><i className={`${action.icon} mr-2`}></i>{action.label}</>
                          )}
                        </span>
                      </Button>
                    ))}

                    {/* More actions dropdown */}
                    <div style={{ position: 'relative' }} ref={actionsMenuRef}>
                      <Button
                        className="btn-primary-custom"
                        onClick={() => setActionsMenuOpen(!actionsMenuOpen)}
                        style={{ padding: '0.375rem 0.75rem' }}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>
                          <i className="tim-icons icon-bullet-list-67"></i>
                        </span>
                      </Button>
                      {actionsMenuOpen && (
                        <div style={{
                          position: 'absolute', top: '100%', right: 0,
                          minWidth: '200px', marginTop: '8px',
                          background: '#1e1e2f',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '4px',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                          zIndex: 1060,
                          overflow: 'hidden',
                        }}>
                          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 16px' }}>
                            Actions
                          </div>
                          {(() => {
                            // Role-based menu. Each item declares which
                            // capability flag gates its visibility. Divider
                            // sits between the pre-delivery cluster and
                            // Deliver — hidden together when Deliver is.
                            const allItems: any[] = [
                              { label: 'Upload Spots', icon: 'tim-icons icon-cloud-upload-94', onClick: () => { setActionsMenuOpen(false); history.push(ROUTES.ADD_AUDIO, { fromJobId: jobId }); }, disabled: false, allowed: canUploadSpots },
                              // Enabled whenever there's actual work to do
                              // (unmatchedCount > 0). Dropped the old
                              // `linkedCount > 0` guard — it hid the button
                              // once ANY station was linked, blocking users
                              // from re-matching after deletes/replacements
                              // in the library. `unmatchedCount` now uses
                              // `hasValidLink` so dangling references count
                              // as unmatched and the button re-appears.
                              { label: matching ? 'Matching...' : 'Auto-Match Tracks', icon: 'tim-icons icon-link-72', onClick: () => { setActionsMenuOpen(false); handleAutoMatch(); }, disabled: matching || unmatchedCount === 0, allowed: canAutoMatch },
                              { label: batchQCing ? `QC ${batchQCProgress.current}/${batchQCProgress.total}` : `QC All${qcPassedCount > 0 ? ` (${qcPassedCount}/${pendingLinkedTrackData.length} passed)` : ''}`, icon: 'tim-icons icon-check-2', onClick: () => { setActionsMenuOpen(false); handleBatchQC(); }, disabled: batchQCing || linkedCount === 0, allowed: canBatchQc },
                              { label: batchReviewing ? 'Reviewing...' : `Review All${approvedCount > 0 ? ` (${approvedCount}/${pendingLinkedTrackData.length} done)` : ''}`, icon: 'tim-icons icon-shape-star', onClick: () => { setActionsMenuOpen(false); handleBatchReviewOpen(); }, disabled: batchReviewing || linkedCount === 0, allowed: canBatchReview },
                              { label: 'Edit Campaign', icon: 'tim-icons icon-pencil', onClick: () => { setActionsMenuOpen(false); setShowEditor(true); }, disabled: false, allowed: canEditCampaign },
                              { divider: true, allowed: canDeliver },
                              { label: sharing
                                ? 'Sharing...'
                                : (hasActiveDelivery && pendingCount === 0)
                                  ? 'Delivered'
                                  : isPartialRedeliver
                                    ? `Deliver ${pendingCount} New Station${pendingCount === 1 ? '' : 's'}`
                                    : allDeliveriesRejected ? 'Re-Deliver' : 'Deliver',
                                icon: 'tim-icons icon-send',
                                onClick: () => { setActionsMenuOpen(false); handleShareJob(); },
                                disabled: sharing || readyCount === 0 || !readyToDeliver,
                                hint: (hasActiveDelivery && pendingCount === 0)
                                  ? 'All stations on this campaign have been delivered'
                                  : !readyToDeliver && pendingCount > 0
                                    ? (!qcGateOk && !reviewGateOk ? 'All new stations must pass QC and be approved' : !qcGateOk ? 'All new stations must pass QC' : 'All new stations must be approved')
                                    : undefined,
                                allowed: canDeliver },
                            ];
                            return allItems.filter((item) => item.allowed);
                          })().map((item: any, i) => item.divider ? (
                            <div key={i} style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '4px 0' }} />
                          ) : (
                            <button
                              key={i}
                              onClick={item.onClick}
                              disabled={item.disabled}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                                padding: '10px 16px', background: 'none', border: 'none',
                                color: item.disabled ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.9)',
                                fontSize: '14px', cursor: item.disabled ? 'not-allowed' : 'pointer',
                                textAlign: 'left', transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => { if (!item.disabled) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <i className={item.icon} style={{ fontSize: '14px', width: '18px', textAlign: 'center' }}></i>
                              <div>
                                <div>{item.label}</div>
                                {item.hint && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '1px' }}>{item.hint}</div>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Partial-redeliver banner — surfaces the "prior delivery
                exists AND new stations added since" state so the operator
                knows what the CTA will do. Only shown on the source side
                (received copies never see the deliver button). */}
            {!isReceivedJob && isPartialRedeliver && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.6rem 0.9rem',
                  background: 'rgba(80, 200, 120, 0.08)',
                  border: '1px solid rgba(80, 200, 120, 0.35)',
                  borderRadius: 6,
                  color: '#50c878',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                }}
              >
                <i className="tim-icons icon-alert-circle-exc" />
                <span>
                  <strong>{pendingCount}</strong> new station{pendingCount === 1 ? '' : 's'} added since the last delivery.
                  {' '}Only the new station{pendingCount === 1 ? '' : 's'} will be sent when you deliver —
                  {' '}already-delivered stations stay where they are.
                </span>
              </div>
            )}
            {/* Delivery overrides banner — shown when the current org has
                bypassed one or both delivery gates via Admin → Delivery Overrides */}
            {!isReceivedJob && (bypassQc || bypassReview) && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.6rem 0.9rem',
                  background: 'rgba(255, 180, 50, 0.1)',
                  border: '1px solid rgba(255, 180, 50, 0.35)',
                  borderRadius: 6,
                  color: '#ffb432',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                }}
              >
                <i className="tim-icons icon-alert-circle-exc" />
                <span>
                  <strong>Delivery overrides active:</strong>{' '}
                  {[bypassQc && 'QC bypassed', bypassReview && 'Review bypassed'].filter(Boolean).join(' · ')}
                  {' '}
                  <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                    — campaigns can be delivered without the matching check passing.
                  </span>
                </span>
              </div>
            )}

            {/* Match result banner */}
            {matchResult && (
              <div style={{
                marginBottom: '1rem', padding: '0.75rem 1rem',
                backgroundColor: matchResult.includes('All stations matched') ? 'rgba(80, 200, 120, 0.1)' : 'rgba(255, 180, 50, 0.1)',
                borderRadius: '6px',
                border: `1px solid ${matchResult.includes('All stations matched') ? 'rgba(80, 200, 120, 0.3)' : 'rgba(255, 180, 50, 0.3)'}`,
                color: matchResult.includes('All stations matched') ? '#50c878' : '#ffb432',
                fontSize: '0.9rem',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>{matchResult}</span>
                <button onClick={() => setMatchResult(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1.1rem' }}>&times;</button>
              </div>
            )}

            {/* Share result banner */}
            {shareResult && (
              <div style={{
                marginBottom: '1rem', padding: '0.75rem 1rem',
                backgroundColor: shareResult.errors.length > 0 ? 'rgba(255, 180, 50, 0.1)' : 'rgba(80, 200, 120, 0.1)',
                borderRadius: '6px',
                border: `1px solid ${shareResult.errors.length > 0 ? 'rgba(255, 180, 50, 0.3)' : 'rgba(80, 200, 120, 0.3)'}`,
                color: shareResult.errors.length > 0 ? '#ffb432' : '#50c878',
                fontSize: '0.9rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    Delivered to {shareResult.deliveries.length} recipient{shareResult.deliveries.length !== 1 ? 's' : ''}.
                    {shareResult.deliveries.filter(d => d.broadcasterOrgId).length > 0 &&
                      ` ${shareResult.deliveries.filter(d => d.broadcasterOrgId).length} in-app, `}
                    {shareResult.deliveries.filter(d => !d.broadcasterOrgId).length > 0 &&
                      ` ${shareResult.deliveries.filter(d => !d.broadcasterOrgId).length} via link only.`}
                  </span>
                  <button onClick={() => setShareResult(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1.1rem' }}>&times;</button>
                </div>
              </div>
            )}

            {/* Review link section */}
            {(job as any).batchReviewLink && (
              <Card className="no-bg-card mb-3">
                <CardBody style={{ padding: '1rem' }}>
                  <h5 style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                    <i className="tim-icons icon-shape-star mr-2" style={{ color: 'var(--button-warning)' }}></i>
                    Review Link
                  </h5>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '4px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ color: '#fff', fontSize: '0.85rem' }}>
                        Shared with <strong>{(job as any).reviewSharedTo || 'external link'}</strong>
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText((job as any).batchReviewLink);
                        const el = document.getElementById('copy-review-link');
                        if (el) { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'Copy Link'; }, 2000); }
                      }}
                      style={{
                        background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                        color: 'var(--color-accent)', cursor: 'pointer',
                        fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px',
                      }}
                    >
                      <span id="copy-review-link">Copy Link</span>
                    </button>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Activity panel — collapsible campaign history. Shows every
                edit / auto-match / unlink / delivery event, sourced from
                the /jobs/{jobId}/history subcollection on the source. */}
            {historyEntries.length > 0 && (
              <Card className="no-bg-card mb-3">
                <CardBody style={{ padding: '0.75rem 1rem' }}>
                  <button
                    onClick={() => setHistoryExpanded(v => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', background: 'none', border: 'none', padding: 0,
                      cursor: 'pointer', color: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: 500 }}>
                      <i className={`tim-icons ${historyExpanded ? 'icon-minimal-down' : 'icon-minimal-right'}`} style={{ color: 'var(--accent-1-color)', fontSize: '0.85rem' }}></i>
                      Activity ({historyEntries.length})
                    </span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                      Last update {historyEntries[0] ? moment(historyEntries[0].timestamp).fromNow() : ''}
                    </span>
                  </button>
                  {historyExpanded && (
                    <div style={{ marginTop: '0.75rem', maxHeight: '360px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                      {historyEntries.map((entry, i) => (
                        <div key={entry.id} style={{
                          padding: '0.6rem 0',
                          borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                              <strong>{entry.actorName || 'Someone'}</strong>
                              <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: '0.35rem' }}>
                                · {entry.actorOrgName || 'Unknown org'}
                                {entry.actorOrgRole ? ` · ${entry.actorOrgRole}` : ''}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }} title={new Date(entry.timestamp).toLocaleString()}>
                              {moment(entry.timestamp).fromNow()}
                            </div>
                          </div>
                          <ul style={{ margin: '0.35rem 0 0 1.1rem', padding: 0, listStyle: 'disc' }}>
                            {(entry.changes || []).map((c, ci) => (
                              <li key={ci} style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.72)', marginBottom: '2px', lineHeight: 1.45 }}>
                                {describeChange(c)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Per-campaign Delivery-FTP opt-in + catch-up. Only where this view
                can actually deliver (source or studio-role copy — never received
                jobs or agency copies), so deliveries + ftpDeliveryState live
                under this org/jobId. Renders nothing unless the org has Delivery
                FTP enabled. */}
            {canDeliver && !isReceivedJob && (
              <FtpCampaignControl
                ref={ftpControlRef}
                orgId={currentOrgId || ''}
                jobId={jobId}
                client={campaignClient}
                codes={campaignFtpCodes}
                sentCodes={Array.isArray(ftpDeliveryState?.sentCodes) ? ftpDeliveryState.sentCodes : []}
                pendingCount={pendingCount}
                hasPriorDelivery={hasPriorDelivery}
              />
            )}

            {/* Delivery links section */}
            {deliveries.length > 0 && (
              <Card className="no-bg-card mb-3">
                <CardBody style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', gap: '1rem', flexWrap: 'wrap' }}>
                    <h5 style={{ margin: 0, fontSize: '0.95rem' }}>
                      <i className="tim-icons icon-send mr-2" style={{ color: 'var(--accent-1-color)' }}></i>
                      Deliveries ({deliveries.length})
                    </h5>
                    <DeliveryReportButton
                      job={job}
                      deliveries={deliveries}
                      sourceOrgName={currentOrgName || ''}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {deliveries.map((d: any) => {
                      // Phase 2 — cascade may have marked individual stations
                      // deleted (`deleted: true`) or the whole delivery
                      // deleted (`status: 'deleted'`). Show live counts, and
                      // surface deletion with a badge + strike-through
                      // instead of hiding the delivery entirely — the
                      // operator needs to see the audit trail.
                      const liveStations = Array.isArray(d.stations)
                        ? d.stations.filter((s: any) => !s?.deleted)
                        : [];
                      const stationCount = Array.isArray(d.stations)
                        ? liveStations.length
                        : d.stationCount || 0;
                      const trackCount = Array.isArray(d.stations)
                        ? new Set(liveStations.filter((s: any) => s.linkedTrackId).map((s: any) => s.linkedTrackId)).size
                        : d.trackCount || d.tracksCopied || 0;
                      const entityLabel = d.isMediaHouse ? 'media house' : 'station';
                      // Set of trackIds the broadcaster has actually
                      // downloaded (recorded by stampDeliveryDownload
                      // CF). Used to green-tick each flighting code
                      // whose linked track has been grabbed.
                      const downloadedTrackIdSet = new Set<string>(
                        Array.isArray(d.downloadedTrackIds) ? d.downloadedTrackIds : [],
                      );
                      const flightingChips = liveStations
                        .filter((s: any) => s.flightingCode)
                        .map((s: any, idx: number) => {
                          const isDownloaded = !!(s.linkedTrackId && downloadedTrackIdSet.has(s.linkedTrackId));
                          return (
                            <span
                              key={`${s.id || idx}-${s.flightingCode}`}
                              style={{
                                color: isDownloaded ? '#50c878' : 'rgba(255,255,255,0.4)',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                              }}
                              title={isDownloaded ? 'Downloaded' : 'Not yet downloaded'}
                            >
                              {isDownloaded && <span style={{ marginRight: '3px' }}>✓</span>}
                              {s.flightingCode}
                            </span>
                          );
                        });
                      const isDeleted = d.status === 'deleted';
                      return (
                        <div key={d.deliveryId || d.id} style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '4px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <span style={{ color: '#fff', fontWeight: '500', fontSize: '0.85rem' }}>
                                {d.broadcasterOrgName}
                              </span>
                              {d.broadcasterOrgId ? (
                                <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.2)', color: '#50c878', fontSize: '0.65rem' }}>In-App</Badge>
                              ) : (
                                <Badge style={{ backgroundColor: 'rgba(255, 180, 50, 0.2)', color: '#ffb432', fontSize: '0.65rem' }}>Link Only</Badge>
                              )}
                              {/* [FTP] The FTP push is shown as its own
                                  standalone delivery row below, not as a badge
                                  on each station delivery. */}
                              <Badge style={{
                                backgroundColor: d.status === 'rejected' ? 'rgba(255, 80, 80, 0.2)'
                                  : isDeleted ? 'rgba(140, 140, 140, 0.2)'
                                  : 'rgba(80, 200, 120, 0.2)',
                                color: d.status === 'rejected' ? '#ff5050'
                                  : isDeleted ? '#aaaaaa'
                                  : '#50c878',
                                fontSize: '0.65rem',
                              }}>
                                {d.status === 'rejected' ? 'Rejected'
                                  : isDeleted ? 'Deleted'
                                  : 'Delivered'}
                              </Badge>
                              {/* Download status — only when the delivery
                                  has actually been downloaded. Green for
                                  complete, yellow for partial (some files
                                  failed to fetch on the broadcaster's
                                  side). Written by the stampDeliveryDownload
                                  Cloud Function when the broadcaster hits
                                  Download or FTP-send in DownloadManagerModal. */}
                              {d.status !== 'rejected' && !isDeleted && d.downloadedAt && (
                                <Badge style={{
                                  backgroundColor: d.downloadStatus === 'partial'
                                    ? 'rgba(255, 180, 50, 0.2)'
                                    : 'rgba(80, 200, 120, 0.2)',
                                  color: d.downloadStatus === 'partial' ? '#ffb432' : '#50c878',
                                  fontSize: '0.65rem',
                                }}
                                  title={`${d.downloadFileCount || 0}/${d.downloadTotalCount || 0} files via ${d.downloadMethod || 'download'}`}>
                                  {d.downloadStatus === 'partial' ? 'Downloaded · partial' : 'Downloaded'}
                                </Badge>
                              )}
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                                {stationCount} {entityLabel}{stationCount !== 1 ? 's' : ''} · {trackCount} track{trackCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                const link = `${window.location.origin}/delivery/${d.deliveryCode}`;
                                navigator.clipboard.writeText(link);
                                const el = document.getElementById(`copy-${d.deliveryCode}`);
                                if (el) { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'Copy Link'; }, 2000); }
                              }}
                              style={{
                                background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                                color: 'var(--accent-1-color)', cursor: 'pointer',
                                fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px',
                              }}
                            >
                              <span id={`copy-${d.deliveryCode}`}>Copy Link</span>
                            </button>
                          </div>
                          {flightingChips.length > 0 && (
                            <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {flightingChips}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Standalone Delivery-FTP row — the campaign's assets pushed
                        to the org's external FTP, shown as its own "delivery"
                        (host + codes sent) rather than a badge on each station. */}
                    {(() => {
                      const st = ftpDeliveryState;
                      const sentCodes: string[] = Array.isArray(st?.sentCodes) ? st.sentCodes : [];
                      const failedCodes: string[] = Array.isArray(st?.failedCodes) ? st.failedCodes : [];
                      if (!st || (sentCodes.length === 0 && failedCodes.length === 0)) return null;
                      const status = failedCodes.length === 0 ? 'sent'
                        : (sentCodes.length > 0 ? 'partial' : 'failed');
                      const statusMap: Record<string, { bg: string; color: string; label: string }> = {
                        sent: { bg: 'rgba(90, 170, 255, 0.2)', color: '#5aaaff', label: `FTP · ${sentCodes.length}` },
                        partial: { bg: 'rgba(255, 180, 50, 0.2)', color: '#ffb432', label: `FTP · partial (${sentCodes.length} sent, ${failedCodes.length} failed)` },
                        failed: { bg: 'rgba(255, 80, 80, 0.2)', color: '#ff5050', label: 'FTP · failed' },
                      };
                      const m = statusMap[status];
                      return (
                        <div style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'rgba(90, 170, 255, 0.06)', borderRadius: '4px',
                          border: '1px solid rgba(90, 170, 255, 0.15)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <span style={{ color: '#fff', fontWeight: '500', fontSize: '0.85rem' }}>
                              <i className="tim-icons icon-cloud-upload-94 mr-2" style={{ color: '#5aaaff' }}></i>
                              {st.host || 'FTP server'}
                            </span>
                            <Badge style={{ backgroundColor: m.bg, color: m.color, fontSize: '0.65rem' }}>
                              {m.label}
                            </Badge>
                            {st.protocol && (
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                {st.protocol}{st.path ? ` · ${st.path}` : ''}
                              </span>
                            )}
                            {st.updatedAt && (
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                                {new Date(st.updatedAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                          {(sentCodes.length > 0 || failedCodes.length > 0) && (
                            <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {sentCodes.map((code) => (
                                <span key={`ftp-ok-${code}`} title="Sent to FTP" style={{ color: '#50c878', marginRight: '0.5rem', whiteSpace: 'nowrap' }}>
                                  ✓ {code}
                                </span>
                              ))}
                              {failedCodes.map((code) => (
                                <span key={`ftp-fail-${code}`} title="Failed — will retry on re-delivery" style={{ color: '#ff5050', marginRight: '0.5rem', whiteSpace: 'nowrap' }}>
                                  ✗ {code}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Summary cards */}
            <Row className="mb-3">
              <Col md="3">
                <Card className="no-bg-card">
                  <CardBody style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--accent-1-color)' }}>{job.stationCount}</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Total Stations</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="no-bg-card">
                  <CardBody style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: '600', color: linkedCount === job.stationCount ? '#50c878' : '#ffb432' }}>
                      {linkedCount}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Linked Tracks</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="no-bg-card">
                  <CardBody style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: '600', color: unmatchedCount > 0 ? '#ff5050' : '#50c878' }}>
                      {unmatchedCount}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Unmatched</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="3">
                <Card className="no-bg-card">
                  <CardBody style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '500', color: 'rgba(255,255,255,0.5)' }}>
                      {job.fileName}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Source File</div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Station list */}
            <Card className="no-bg-card">
              <CardBody>
                <h5 style={{ marginBottom: '1rem' }}>Station Entries</h5>
                <div style={{ overflowX: 'auto' }}>
                  <Table dark responsive size="sm" style={{ backgroundColor: 'transparent' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Station</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Media House</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Language</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Duration</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Flighting Code</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Deadline</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Linked Track</th>
                        <th style={{ border: 'none', padding: '0.5rem' }}>Status</th>
                        <th style={{ border: 'none', padding: '0.5rem', width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.stations.map((station, idx) => {
                        const linkedTrack = station.linkedTrackId ? getTrackInfo(station.linkedTrackId) : null;
                        // Per-station download-status badge — reads the
                        // broadcasterDownloadStatus map written by the
                        // stampDeliveryDownload CF (mirrored to every
                        // job copy). Only shown when the station has
                        // a linked recipient org AND the map has an
                        // entry for them (missing entry = delivery
                        // hasn't happened yet, so no download status
                        // to show).
                        const downloadStatusMap = (job as any).broadcasterDownloadStatus || {};
                        const stationDownloadStatus = (station as any).stationOrgId
                          ? downloadStatusMap[(station as any).stationOrgId]
                          : undefined;
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                            <td style={{ border: 'none', padding: '0.5rem', color: '#fff', fontWeight: '500' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                {station.station}
                                {(station as any).stationOrgId && (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '1px 5px', fontSize: '0.6rem', borderRadius: '3px',
                                    backgroundColor: 'rgba(80, 200, 120, 0.15)', color: '#50c878',
                                    fontWeight: '600', letterSpacing: '0.3px', whiteSpace: 'nowrap',
                                  }} title={`Will deliver to ${(station as any).stationOrgName || station.station}`}>
                                    <i className="tim-icons icon-send" style={{ fontSize: '0.5rem' }}></i>
                                    DELIVER
                                  </span>
                                )}
                                {stationDownloadStatus === 'complete' && (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '1px 5px', fontSize: '0.6rem', borderRadius: '3px',
                                    backgroundColor: 'rgba(80, 200, 120, 0.2)', color: '#50c878',
                                    fontWeight: '600', letterSpacing: '0.3px', whiteSpace: 'nowrap',
                                  }} title={`${(station as any).stationOrgName || station.station} downloaded their delivery`}>
                                    ✓ DOWNLOADED
                                  </span>
                                )}
                                {stationDownloadStatus === 'partial' && (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '1px 5px', fontSize: '0.6rem', borderRadius: '3px',
                                    backgroundColor: 'rgba(255, 180, 50, 0.2)', color: '#ffb432',
                                    fontWeight: '600', letterSpacing: '0.3px', whiteSpace: 'nowrap',
                                  }} title={`${(station as any).stationOrgName || station.station} has partially downloaded their delivery`}>
                                    ◐ PARTIAL
                                  </span>
                                )}
                                {stationDownloadStatus === 'none' && (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '1px 5px', fontSize: '0.6rem', borderRadius: '3px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'rgba(255,255,255,0.55)',
                                    fontWeight: '600', letterSpacing: '0.3px', whiteSpace: 'nowrap',
                                  }} title={`${(station as any).stationOrgName || station.station} has not yet downloaded their delivery`}>
                                    ○ NOT DOWNLOADED
                                  </span>
                                )}
                              </span>
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                {station.mediaOwner || '--'}
                                {(station as any).mediaOwnerOrgId && (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '1px 5px', fontSize: '0.6rem', borderRadius: '3px',
                                    backgroundColor: 'rgba(80, 200, 120, 0.15)', color: '#50c878',
                                    fontWeight: '600', letterSpacing: '0.3px', whiteSpace: 'nowrap',
                                  }} title={`Will deliver to ${(station as any).mediaOwnerOrgName || station.mediaOwner}`}>
                                    <i className="tim-icons icon-send" style={{ fontSize: '0.5rem' }}></i>
                                    DELIVER
                                  </span>
                                )}
                              </span>
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
                              {Array.isArray(station.language)
                                ? station.language.map((l: any) => typeof l === 'object' ? l.label : l).join(', ')
                                : station.language || '--'}
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
                              {station.duration}
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem' }}>
                              <span style={{
                                color: station.flightingCode === 'TBA' ? '#ffb432' : 'var(--accent-1-color)',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                              }}>
                                {station.flightingCode}
                              </span>
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                              {station.materialDeadline || '--'}
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem' }}>
                              {linkedTrack ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  <span
                                    style={{ color: '#50c878', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(80, 200, 120, 0.3)', textUnderlineOffset: '2px' }}
                                    title="View in library"
                                    onClick={() => history.push(ROUTES.FIND_AUDIO, { groupByCampaign: job.campaign })}
                                  >
                                    {linkedTrack.trackName}
                                  </span>
                                  {linkedTrack.fileVersion > 0 && (
                                    <VersionBadge type="version" version={linkedTrack.fileVersion} status={linkedTrack.approval_status} />
                                  )}
                                  {linkedTrack.qcVersion >= 1 && (linkedTrack.qc_status === 'pass' || linkedTrack.qc_status === 'fail' || linkedTrack.qc_status === 'testing') && (
                                    <VersionBadge type="qc" version={linkedTrack.qcVersion} status={linkedTrack.qc_status} />
                                  )}
                                  {!isReceivedJob && (station as any).deliveryStatus !== 'delivered' && (
                                    <button
                                      onClick={() => handleUnlink(idx)}
                                      style={{
                                        background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
                                        cursor: 'pointer', fontSize: '0.9rem', padding: '0 2px',
                                      }}
                                      title="Unlink track"
                                    >
                                      &times;
                                    </button>
                                  )}
                                </div>
                              ) : (station as any).isLiveRead ? (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  padding: '2px 8px', fontSize: '0.7rem', borderRadius: '4px',
                                  backgroundColor: 'rgba(140, 100, 220, 0.18)',
                                  color: '#b394ff',
                                  fontWeight: '600', letterSpacing: '0.3px',
                                }} title="Live Read — read on-air by the DJ; no audio to link">
                                  <i className="tim-icons icon-sound-wave" style={{ fontSize: '0.6rem' }}></i>
                                  LIVE READ
                                </span>
                              ) : station.linkedTrackId ? (
                                // Dangling link — station's linkedTrackId no longer
                                // resolves to a track in the library (deleted or
                                // moved). Show explicit signal + × so the user can
                                // clear it manually if Auto-Match can't find a
                                // replacement. Auto-Match will also treat this row
                                // as unmatched and attempt to re-resolve on click.
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  <span
                                    title="The track this station was linked to has been removed from the library. Run Auto-Match to try to re-link, or click × to clear the broken link."
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                                      padding: '2px 8px', fontSize: '0.7rem', borderRadius: '4px',
                                      backgroundColor: 'rgba(255, 80, 80, 0.15)',
                                      color: '#ff8080',
                                      fontWeight: '600', letterSpacing: '0.3px',
                                    }}
                                  >
                                    <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '0.65rem' }}></i>
                                    TRACK MISSING
                                  </span>
                                  {!isReceivedJob && (station as any).deliveryStatus !== 'delivered' && (
                                    <button
                                      onClick={() => handleUnlink(idx)}
                                      style={{
                                        background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
                                        cursor: 'pointer', fontSize: '0.9rem', padding: '0 2px',
                                      }}
                                      title="Clear broken link"
                                    >
                                      &times;
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' }}>
                                  Not linked
                                </span>
                              )}
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                {linkedTrack && getApprovalBadge(linkedTrack)}
                                {linkedTrack && getQCBadge(linkedTrack)}
                                {!linkedTrack && (station as any).isLiveRead && (
                                  <Badge style={{ backgroundColor: 'rgba(140, 100, 220, 0.18)', color: '#b394ff', fontSize: '0.65rem' }}>
                                    N/A
                                  </Badge>
                                )}
                                {!linkedTrack && !(station as any).isLiveRead && (
                                  <Badge style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
                                    Awaiting
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td style={{ border: 'none', padding: '0.5rem' }}>
                              {(() => {
                                const mp3Url = linkedTrack?.mp3Files?.[0]?.downloadUrl
                                  || (station as any).mp3Files?.[0]?.downloadUrl;
                                if (!mp3Url) return null;
                                const isPlaying = playingStationIdx === idx;
                                return (
                                  <button
                                    onClick={() => setPlayingStationIdx(isPlaying ? null : idx)}
                                    style={{
                                      background: 'none',
                                      border: '1px solid rgba(255,255,255,0.2)',
                                      color: isPlaying ? 'var(--accent-1-color)' : 'rgba(255,255,255,0.5)',
                                      cursor: 'pointer',
                                      fontSize: '0.75rem',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    {isPlaying ? 'Close' : 'Play'}
                                  </button>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {/* Audio player for active track */}
                  {playingStationIdx !== null && (() => {
                    const station = job.stations[playingStationIdx];
                    const linkedTrack = station?.linkedTrackId ? getTrackInfo(station.linkedTrackId) : null;
                    const mp3Url = linkedTrack?.mp3Files?.[0]?.downloadUrl
                      || (station as any)?.mp3Files?.[0]?.downloadUrl;
                    const trackName = linkedTrack?.trackName || (station as any)?.trackName || station?.flightingCode || '';
                    if (!mp3Url) return null;
                    return (
                      <div style={{
                        marginTop: '1rem', padding: '1rem',
                        backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}>
                        <div style={{ marginBottom: '0.5rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                          Now playing: <strong style={{ color: '#fff' }}>{trackName}</strong>
                          {' '}&mdash; {station.station}
                        </div>
                        <WavePlayer
                          id={`previewAudioPlayer-job-${playingStationIdx}`}
                          file={mp3Url}
                          noAutoplay
                          isActiveTrack={true}
                        />
                      </div>
                    );
                  })()}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Batch Review Modal */}
      <Modal
        isOpen={showBatchReviewModal}
        toggle={() => { if (!batchReviewing) setShowBatchReviewModal(false); }}
        style={{ maxWidth: '500px', margin: '1.75rem auto' }}
        contentClassName="delete-confirm-modal"
      >
        <ModalHeader
          toggle={() => { if (!batchReviewing) setShowBatchReviewModal(false); }}
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          close={
            <button className="close" onClick={() => setShowBatchReviewModal(false)} style={{
              color: 'var(--color-accent)', opacity: 1, textShadow: 'none',
              background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
            }}>
              &times;
            </button>
          }
        >
          <span style={{ color: '#fff' }}>
            <i className="tim-icons icon-shape-star mr-2" style={{ color: 'var(--color-accent)' }}></i>
            Share for Review
          </span>
        </ModalHeader>
        <ModalBody style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
        }}>
          {!batchReviewLink ? (
            <>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Share all unreviewed spots from this campaign for client approval. A single review link will be generated.
              </p>

              {/* Share target search */}
              <Label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>Share with (optional)</Label>
              <Input
                bsSize="sm"
                placeholder="Search for a client or agency..."
                value={batchReviewTargetSearch}
                onChange={(e) => {
                  setBatchReviewTargetSearch(e.target.value);
                  if (!e.target.value.trim()) setBatchReviewTarget(null);
                }}
                style={{
                  backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  color: batchReviewTarget ? 'var(--button-success)' : '#fff', fontSize: '0.85rem', marginBottom: '0.25rem',
                }}
              />
              {batchReviewTargetOptions.length > 0 && !batchReviewTarget && (
                <div style={{
                  maxHeight: '150px', overflowY: 'auto', marginBottom: '0.75rem',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px',
                  backgroundColor: '#1e1e2f',
                }}>
                  {batchReviewTargetOptions.map((opt: any) => (
                    <div
                      key={`${opt.type}-${opt.id}`}
                      onClick={() => {
                        setBatchReviewTarget(opt);
                        setBatchReviewTargetSearch(opt.name);
                        setBatchReviewTargetOptions([]);
                      }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem',
                        color: 'rgba(255,255,255,0.9)', transition: 'background-color 0.15s',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <span>{opt.name}</span>
                      <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>
                        {opt.type}{opt.hasOrg ? ' (in-app)' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {batchReviewTarget && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem',
                  padding: '6px 10px', borderRadius: '4px',
                  backgroundColor: 'rgba(80,200,120,0.1)', border: '1px solid rgba(80,200,120,0.2)',
                }}>
                  <span style={{ color: 'var(--button-success)', fontSize: '0.8rem', flex: 1 }}>
                    {batchReviewTarget.name}
                    {batchReviewTarget.hasOrg && <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: '6px' }}>(will receive in-app)</span>}
                  </span>
                  <button onClick={() => { setBatchReviewTarget(null); setBatchReviewTargetSearch(''); }} style={{
                    background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1rem',
                  }}>&times;</button>
                </div>
              )}

              <Label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: '0.5rem' }}>Notes for reviewer</Label>
              <Input
                type="textarea"
                bsSize="sm"
                rows={2}
                value={batchReviewNotes}
                onChange={(e) => setBatchReviewNotes(e.target.value)}
                placeholder="Any notes for the reviewer..."
                style={{
                  backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff', fontSize: '0.85rem', resize: 'none',
                }}
              />
            </>
          ) : (
            /* Success state — show link */
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <i className="tim-icons icon-check-2" style={{ fontSize: '2rem', color: 'var(--button-success)', display: 'block', marginBottom: '0.75rem' }}></i>
              <p style={{ color: '#fff', fontWeight: 500, marginBottom: '0.5rem' }}>Review link generated</p>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem',
                padding: '8px 12px', borderRadius: '6px',
                backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              }}>
                <input
                  readOnly
                  value={batchReviewLink}
                  style={{
                    flex: 1, background: 'none', border: 'none', color: 'var(--color-accent)',
                    fontSize: '0.8rem', fontFamily: 'monospace', outline: 'none',
                  }}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(batchReviewLink!);
                    const el = document.getElementById('copy-batch-review-link');
                    if (el) { el.textContent = 'Copied!'; setTimeout(() => { el.textContent = 'Copy'; }, 2000); }
                  }}
                  style={{
                    background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                    color: 'var(--color-accent)', cursor: 'pointer',
                    fontSize: '0.75rem', padding: '3px 8px', borderRadius: '4px',
                  }}
                >
                  <span id="copy-batch-review-link">Copy</span>
                </button>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                Share this link with your client. They can review and approve all spots from one page.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'flex-end',
          gap: '0.75rem',
        }}>
          {!batchReviewLink ? (
            <>
              <Button className="btn-danger-custom" onClick={() => setShowBatchReviewModal(false)} size="sm" disabled={batchReviewing}>
                <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
              </Button>
              <Button className="btn-success-custom" onClick={handleBatchReviewSubmit} size="sm" disabled={batchReviewing}>
                <span style={{ position: 'relative', zIndex: 2 }}>
                  {batchReviewing ? (
                    <><Spinner size="sm" className="mr-1" /> Sharing...</>
                  ) : (
                    <><i className="tim-icons icon-send mr-1"></i>Share for Review</>
                  )}
                </span>
              </Button>
            </>
          ) : (
            <Button className="btn-primary-custom" onClick={() => setShowBatchReviewModal(false)} size="sm">
              <span style={{ position: 'relative', zIndex: 2 }}>Done</span>
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Edit Job Modal */}
      <ChaseListUploader
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        firebase={firebase}
        editJob={job ? {
          id: job.id,
          header: {
            client: job.client || '',
            campaign: job.campaign || '',
            period: job.period || '',
            date: job.date || '',
            revision: job.revision || '',
            agencyContacts: job.agencyContacts || [],
            // Studio + agency tags need to round-trip or the save path
            // silently blanks them on the source doc — which then breaks
            // syncCampaignCopies (no targets to write to = copies never
            // receive the edit).
            studioOrgId: (job as any).studioOrgId || '',
            studioName: (job as any).studioName || '',
            agencyOrgId: (job as any).agencyOrgId || '',
            agencyName: (job as any).agencyName || '',
          },
          stations: job.stations,
          // Round-trip the current status so the save-path can preserve
          // it when the diff reports no invalidating station changes.
          // Without this, header-only edits (add / remove agency, fix a
          // typo) unconditionally bounce delivered campaigns to draft.
          status: (job as any).status,
          // Round-trip the pending-updates flag so a save that DOESN'T
          // trigger new field edits preserves the existing flag rather
          // than clearing it. (User might edit twice before clicking
          // Publish Updates.)
          hasPendingUpdates: (job as any).hasPendingUpdates,
          // Carry copy metadata so the editor can redirect its write
          // upstream (agency editing their copy still updates the source
          // + fans out to all copies).
          isCampaignCopy: isCampaignCopy,
          sourceOrgId: (job as any).sourceOrgId,
          sourceJobId: (job as any).sourceJobId,
          copyRole: jobCopyRole,
          // Original upload metadata — carried through so if the sync
          // re-writes a fresh copy (e.g. after re-tagging studio/agency),
          // the copy has the same provenance fields as the source.
          fileName: (job as any).fileName,
          sheetName: (job as any).sheetName,
          uploadedAt: (job as any).uploadedAt,
          uploadedBy: (job as any).uploadedBy,
          uploadedByName: (job as any).uploadedByName,
        } : null}
        onSaved={async (savedJobId) => {
          setShowEditor(false);
          // Reload the job data
          if (currentOrgId && firebase) {
            const jobRef = doc(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'), savedJobId);
            const jobDoc = await getDoc(jobRef);
            if (jobDoc.exists()) {
              setJob({ id: jobDoc.id, ...jobDoc.data() } as JobData);
            }
          }
        }}
      />

      {/* Download Manager Modal */}
      <DownloadManagerModal
        isOpen={showDownloadManager}
        onClose={() => setShowDownloadManager(false)}
        zipName={job?.campaign || 'spots'}
        // When the current view is a received / delivered job (broadcaster
        // side), pass the delivery context so the modal reports download
        // completion back to the source studio via
        // `stampDeliveryDownload`. The studio's Jobs list + JobDetail
        // per-delivery badges then reflect who has grabbed their spots.
        // Omitted for studio-side previews of their own campaign so the
        // studio doesn't accidentally stamp their own delivery records.
        deliveryContext={(() => {
          const ctx = isReceivedJob && job && (job as any).sourceOrgId ? {
            sourceOrgId: (job as any).sourceOrgId as string,
            jobId: (job as any).sourceJobId || jobId,
            broadcasterOrgId: currentOrgId || '',
          } : undefined;
          // TEMP diagnostic — verify context passes correctly to the modal.
          if (showDownloadManager) {
            console.log('[JobDetail] DownloadManager deliveryContext =', ctx, {
              isReceivedJob,
              hasJob: !!job,
              hasSourceOrgId: !!(job as any)?.sourceOrgId,
              sourceOrgIdValue: (job as any)?.sourceOrgId,
              currentOrgId,
            });
          }
          return ctx;
        })()}
        files={(() => {
          if (!job) return [];
          const downloadFiles: any[] = [];
          const seenTrackIds = new Set<string>();
          for (const station of job.stations) {
            if (!station.linkedTrackId || seenTrackIds.has(station.linkedTrackId)) continue;
            seenTrackIds.add(station.linkedTrackId);
            const track = getTrackInfo(station.linkedTrackId);
            // Prefer WAV, fall back to MP3
            const wavFile = track?.wavFiles?.[0];
            const mp3File = track?.mp3Files?.[0];
            // Also check station-level audio snapshots (for received jobs)
            const stationWav = (station as any).wavFiles?.[0];
            const stationMp3 = (station as any).mp3Files?.[0];
            const fileObj = wavFile || stationWav || mp3File || stationMp3;
            if (fileObj?.downloadUrl) {
              const isWav = !!(wavFile || stationWav);
              downloadFiles.push({
                id: station.linkedTrackId,
                originalName: fileObj.fileName || (track?.trackName || station.flightingCode || 'spot') + (isWav ? '.wav' : '.mp3'),
                customName: track?.trackName || (station as any).trackName || station.flightingCode || '',
                downloadUrl: fileObj.downloadUrl,
                fileType: isWav ? 'wav' : 'mp3',
                trackName: track?.trackName || (station as any).trackName || '',
                station: station.station,
              });
            }
          }
          return downloadFiles;
        })()}
      />

      {/* [FTP-MIGRATION 2026-06-15] FTP Delivery pre-check modal removed
          from this flow. Kept commented for revival if needed.
      <FtpDeliveryModal
        firebase={firebase}
        studioOrgId={currentOrgId || ''}
        jobId={jobId}
        isOpen={ftpModalOpen}
        targets={ftpTargets}
        onClose={() => { setFtpModalOpen(false); setFtpTargets([]); }}
        onDeliver={runShareJob}
      />
      */}
    </div>
  );
};

/**
 * Inline button + dropdown menu for downloading the delivery report.
 * Shows only when the campaign has deliveries. Three formats: PDF (default
 * for forwarding to clients), Excel (multi-sheet for further manipulation),
 * CSV (single flat table).
 */
const DeliveryReportButton: React.FC<{
  job: JobData | null;
  deliveries: any[];
  sourceOrgName: string;
}> = ({ job, deliveries, sourceOrgName }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!job || deliveries.length === 0) return null;
  const ctx = { job, deliveries, sourceOrgName };

  const pickFormat = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 6,
          color: '#fff',
          fontSize: '0.8rem',
          padding: '5px 12px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <i className="tim-icons icon-cloud-download-93" />
        Download report
        <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            background: '#1e1f2a',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            minWidth: 180,
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          <button type="button" onClick={pickFormat(() => downloadDeliveryReportPdf(ctx))} style={dropdownItemStyle}>
            PDF <span style={dropdownHintStyle}>proof of delivery</span>
          </button>
          <button type="button" onClick={pickFormat(() => downloadDeliveryReportExcel(ctx))} style={dropdownItemStyle}>
            Excel <span style={dropdownHintStyle}>multi-sheet</span>
          </button>
          <button type="button" onClick={pickFormat(() => downloadDeliveryReportCsv(ctx))} style={dropdownItemStyle}>
            CSV <span style={dropdownHintStyle}>flat table</span>
          </button>
        </div>
      )}
    </div>
  );
};

const dropdownItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 10,
  width: '100%',
  background: 'transparent',
  border: 'none',
  color: '#fff',
  fontSize: '0.85rem',
  padding: '8px 14px',
  textAlign: 'left',
  cursor: 'pointer',
};
const dropdownHintStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.45)',
};

const condition = (authUser: any) => !!authUser;
export default withOrganization(withAuthorization(condition)(JobDetail));
